// The RDx Decks V2 AI Studio — chat-left / canvas-right.
//
// The co-pilot chat is the primary surface: the user briefs in natural language,
// the AI asks the right intake questions (rendered as clickable chips), proposes
// an outline, and produces the deck — each as a typed widget in the thread. The
// canvas previews the live deck (the same vanilla renderer the artifact uses) in
// an iframe. Uploaded documents ground the conversation. Present opens the
// self-contained presenter; Save persists the compiled artifact + model.
//
// Light DOM (createRenderRoot -> this) so the lifted workspace.css/studio.css
// styles apply. Talks to POST /api/decks/chat and /api/decks/{id}/sources.

import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { api, HttpError } from '../api/client';
import type { ChatTurn, ChatWidget, DeckSource, ChatEnvelope, StreamEvent, GalleryImage, MessageFeedbackRating, VisualIssue } from '../api/types';
import type { SlideDeck } from '../lib/slide-model';
import { renderSlide, renderSlides, renderSlidesDoc, imageSlotsOf, resolvedSrc, sanitizeRich, isRichRegion, type DeckOverrides, type DeckRegionStyles, type RegionStyle } from '../lib/render-slides';
import { mountThumbs } from '../lib/thumb';
import { SLIDE_LIBRARY, type LibTemplate } from '../lib/slide-library';
import { type Slide, type SlideBackground, type BgType, type FontAxis, type DeckBrand, safeFont, youtubeId, normaliseSlide } from '../lib/slide-model';
import { KIND_META } from '../lib/kind-meta';
import { THEME_BUNDLES, THEME_IDS, FONT_AXES, bundleFor } from '../lib/themes';
import { measureFit, OVERPACK_THRESHOLD, type FitSlide } from '../lib/fit';
import { buildingPhase } from '../lib/building-phase';
import { setDeckTitle } from '../lib/page-title';
import type { DeckModel } from '../lib/deck-model';
import '../components/live-present-overlay';

interface ThreadMsg {
  role: 'user' | 'assistant';
  content: string;
  widgets?: ChatWidget[];
  phase?: string;
  /** Stable id for this message when one exists; used as the feedback messageRef
   *  in preference to the turn index. Absent on every message the studio creates
   *  today — the ref then falls back to the turn index (see msgRef). */
  id?: string;
}

/** A document the Home composer attached, awaiting upload once a deck exists. */
interface PendingBrief { name: string; mime: string; b64: string }

/** Paper-plane send glyph (an icon, not the word "Send"). */
const SEND_ICON = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 2 11 13"/><path d="M22 2 15 22l-4-9-9-4z"/></svg>`;
/** Stop glyph shown on the send button while a turn is streaming. */
const STOP_ICON = html`<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>`;

/** The abstract-gradient palette offered in the background gallery. */
const ART_OPTIONS: { id: string; name: string }[] = [
  { id: 'slate', name: 'Slate' }, { id: 'sage', name: 'Sage' }, { id: 'dawn', name: 'Dawn' },
  { id: 'dusk', name: 'Dusk' }, { id: 'mist', name: 'Mist' }, { id: 'ember', name: 'Ember' },
  { id: 'ocean', name: 'Ocean' }, { id: 'plum', name: 'Plum' }, { id: 'gold', name: 'Gold' },
  { id: 'forest', name: 'Forest' },
];
/** Human labels for the background TYPE row (subset shown per KIND_META). */
const BG_TYPE_LABELS: Record<string, string> = {
  none: 'None', colour: 'Colour wash', gradient: 'Gradient', image: 'Image', 'image-split': 'Split image', motion: 'Motion', video: 'Video (YouTube)',
};

/** Art-style presets for the image popover. Each maps a friendly label to one of
 *  the five server style enums (imageprompt.go) — except "Custom", which lets the
 *  user type a free-form description (forwarded as styleText). */
const IMG_STYLE_PRESETS: { id: string; label: string }[] = [
  { id: 'editorial-photo', label: 'Photo' },
  { id: 'line-illustration', label: 'Illustration' },
  { id: 'abstract-gradient', label: 'Abstract' },
  { id: '3d-render', label: '3D' },
  { id: 'duotone', label: 'Duotone' },
  { id: 'custom', label: 'Custom' },
];

/** Map a deck theme id (8 bundles) to one of the four image themes the
 *  generator understands. Dark bundles win first; then the warm/coral accent;
 *  then any navy-accent bundle; everything else (paper/light/pure + default)
 *  is light. */
function imageThemeFor(deckThemeId: string): 'light' | 'dark' | 'coral' | 'navy' {
  if (['workplace-slate', 'dark', 'midnight', 'black'].includes(deckThemeId)) return 'dark';
  if (deckThemeId === 'warm') return 'coral';
  if (deckThemeId === 'navy') return 'navy';
  return 'light';
}

/** Brand-ink accents offered in the slide toolbar. A spread of hues so a deck
 *  (or a customer pitch) can pick a close match; any hex still works via the
 *  custom picker, and on-accent ink stays WCAG-legible automatically. */
const ACCENTS = [
  { id: 'emerald', name: 'RDx green', hex: '#00A76F' },
  { id: 'forest', name: 'Forest', hex: '#2F6B3A' },
  { id: 'teal', name: 'Teal', hex: '#0E8C8B' },
  { id: 'ocean', name: 'Ocean', hex: '#1D6FB8' },
  { id: 'sky', name: 'Slate blue', hex: '#2C5470' },
  { id: 'ink', name: 'Workplace ink', hex: '#1E2A3A' },
  { id: 'violet', name: 'Violet', hex: '#6D5BD0' },
  { id: 'plum', name: 'Plum', hex: '#7A3E6E' },
  { id: 'magenta', name: 'Magenta', hex: '#C2247A' },
  { id: 'rose', name: 'Brick', hex: '#A03020' },
  { id: 'coral', name: 'Coral', hex: '#E8603C' },
  { id: 'amber', name: 'Clay', hex: '#B87333' },
  { id: 'gold', name: 'Gold', hex: '#C99700' },
];

@customElement('studio-page')
export class StudioPage extends LitElement {
  @state() private thread: ThreadMsg[] = [];
  @state() private deck: SlideDeck | null = null;
  @state() private deckId = '';
  @state() private input = '';
  @state() private loading = false;
  @state() private error = '';
  @state() private sources: DeckSource[] = [];
  @state() private focusIndex: number | null = null;
  @state() private saving = false;
  @state() private shareUrl = '';
  @state() private sharing = false;
  private pendingBriefs: PendingBrief[] = [];
  // ── editor state ──
  @state() private sel: { region: string; mode: 'menu' | 'ask' } | null = null;
  private selRect: { left: number; top: number; bottom: number } | null = null;
  @state() private askVal = '';
  @state() private diff: { region: string; old: string; html: string } | null = null;
  @state() private aiEditing = false;
  // Image-slot editing: click an image / gradient slot (data-img-region) to
  // change it — generate, pick a gradient, upload, or remove. Region is
  // 'bg' (slide background), 'panel' (side rail), or 'gal:<i>' (gallery cell).
  @state() private imgSel: { region: string } | null = null;
  /** Transient YouTube URL field for the video-background picker (pasted by the
   *  user, parsed to an id on apply). Reset whenever the picker reopens. */
  @state() private bgVideoUrl = '';
  @state() private bgVideoErr = '';
  @state() private imgGenSubject = '';
  @state() private imgBusy = false;
  /** Image popover tab: "gen" = generate/describe, "pick" = choose an existing
   *  image from the owner's gallery. */
  @state() private imgTab: 'gen' | 'pick' = 'gen';
  /** Chosen art-style preset id — one of the five server style enums, except the
   *  "Custom" preset which sets this to 'custom' and uses imgStyleText instead.
   *  Per-popover-open; DEFAULTS from deckImgStyle so the deck's chosen look
   *  carries into a single-slot generate. */
  @state() private imgStyle = 'editorial-photo';
  /** Free-text art-style description, used only when the "Custom" preset is on. */
  @state() private imgStyleText = '';
  /** Deck-level image-style preference — the one consistent art style the whole
   *  deck's imagery leans on. Read + written by the image popover (defaults from
   *  it), generate-all and the pre-generation panel, so a style chosen anywhere
   *  governs everywhere. One of the IMG_STYLE_PRESETS ids (incl. 'custom'). */
  @state() private deckImgStyle = 'editorial-photo';
  /** Free-text art-style description paired with deckImgStyle === 'custom'. */
  @state() private deckImgStyleText = '';
  /** Up-front THEME pick from the pre-generation panel. When set, it OVERRIDES the
   *  AI's chosen meta.theme on the first generated deck — the user's explicit
   *  choice beats the model's. Cleared after it is applied. */
  @state() private preThemeChoice: string | null = null;
  // ── generate-all options (the mini-dialog shown before a whole-deck pass) ──
  /** True while the generate-all options dialog is open. */
  @state() private genAllOpen = false;
  /** Art style chosen in the generate-all options (defaults from deckImgStyle). */
  @state() private genAllStyle = 'editorial-photo';
  /** Free-text style paired with genAllStyle === 'custom'. */
  @state() private genAllStyleText = '';
  /** When on, generate-all REGENERATES slots that already have an image (swap the
   *  whole deck to one consistent style); off keeps the empty-only behaviour. */
  @state() private genAllReplace = false;
  /** When on, the deck theme is mapped to an image theme and passed to
   *  generation (so generated art matches the deck's mood). */
  @state() private imgUseTheme = true;
  /** Lazy-loaded gallery of the owner's existing images (the "pick" tab). */
  @state() private galleryImgs: GalleryImage[] = [];
  /** Gallery load lifecycle — gated so listImages fires once per popover open. */
  @state() private galleryLoading = false;
  private galleryLoaded = false;
  // ── "Edit this card" AI popover ──
  // A Gamma-style per-slide edit affordance: a free-text instruction plus quick
  // actions. The result is parsed, validated against the focused slide's id, and
  // patches ONLY that slide via mutateSlides — never the whole deck.
  @state() private cardEdit = false;
  @state() private cardEditVal = '';
  @state() private cardBusy = false;
  @state() private libOpen = false;
  @state() private libMode: 'insert' | 'replace' = 'insert';
  @state() private notesOpen = false;
  @state() private toolMenu: 'layout' | 'skin' | 'accent' | 'bg' | null = null;
  @state() private themeOpen = false;
  /** Brand-kit panel (customer logo + accent + tint + co-brand) open state. */
  @state() private brandOpen = false;
  /** Filename currently uploading (empty = idle) — drives the composer spinner. */
  @state() private uploading = '';
  /** Slide ids the AI is currently editing — the prototype shimmer plays on each
   *  (focus canvas + rail/grid thumbnails). A set so Phase-3 multi-slide
   *  orchestration can shimmer several at once. */
  @state() private aiBusy: Set<string> = new Set();
  /** Outline slide ids not yet filled with content (streamed first-draft). They
   *  render as shimmering placeholders until their generated slide arrives. */
  @state() private pending: Set<string> = new Set();
  /** True for the duration of a FRESH first-generation turn — gates the
   *  "Building your deck" animation in the canvas (shown INSTEAD of the
   *  pre-theme picker + the half-empty outline grid). Set when a fresh turn
   *  starts; cleared once every outline slide has streamed in (pending drains)
   *  or the turn ends, so the finished deck takes over the canvas. A REFINE turn
   *  on an existing deck leaves this false — its per-slide shimmer is unchanged. */
  @state() private building = false;
  /** Latest server status caption ("Reading your document…" / "Researching…" /
   *  "Planning the outline…") for the PRE-outline building phase, so the canvas
   *  caption tracks the real backend work instead of freezing. Set by a `status`
   *  stream event; cleared on `outline` (the count-driven phase takes over) and
   *  on `done`. */
  @state() private buildingStatus = '';
  /** Title of the most recently landed slide, shown as the streaming-phase detail
   *  ("'Citation Authority'") in place of "Slide N of M". Set on each `slide`
   *  event; cleared on `done`. */
  @state() private buildingTitle = '';
  /** The planner's live "thinking" preamble for the PRE-outline phase, shown as a
   *  small growing text block under the building caption (Task 6). Set (accumulated)
   *  by each `thinking` stream event; cleared on `outline` (the outline has landed)
   *  and on `done`. DISPLAY-ONLY — it never affects the parsed outline. Empty hides
   *  the block. */
  @state() private buildingThinking = '';
  // ── intake form state ──
  // The "right questions" widget is a form: per question the user can pick one or
  // more options AND/OR type their own, then submit every answer in one message
  // (rather than each tap firing a separate turn). Keyed by question text.
  @state() private intakeSel: Record<string, string[]> = {};
  @state() private intakeOther: Record<string, string> = {};
  @state() private intakeDone: Record<string, boolean> = {};
  // Reserved-id intake answers extracted structurally on submit and threaded into
  // the NEXT chat turn so the fresh generation honours the chosen length / framing.
  // Cleared after that turn POSTs so a later turn never resends stale values.
  @state() private pendingSlideCount?: number;
  @state() private pendingFraming?: 'overview' | 'positioning';
  // `yes` is the confirm button's label (default "Delete" — the dialog's
  // original sole use); generate-all passes "Generate" so the action reads right.
  @state() private confirm: { msg: string; onYes: () => void; yes?: string } | null = null;
  // ── whole-deck "Generate images" pass ──
  /** True while the bounded-parallel generate-all loop is running (disables the
   *  button + shows progress). */
  @state() private genAllBusy = false;
  /** Live progress while generating across the deck — `{done, total}`. */
  @state() private genAllProgress = { done: 0, total: 0 };
  /** The canvas-side notice after a generate-all run (a summary or an error). */
  @state() private genNotice: { msg: string; error?: boolean } | null = null;
  // ── progressive per-slide image fill (Task 3) ──
  /** In-flight (or already-filled this run) image slots, keyed by
   *  `${slideId}::${region}`, so a slot is never enqueued / generated twice. SHARED
   *  by both the progressive per-slide path AND the manual generate-all loop so the
   *  two can never double-fill the same slot. (Task 5 reuses this field.) */
  private fillingSlots = new Set<string>();
  /** Number of slots the progressive per-slide fill has STARTED this generation
   *  run — capped at AUTO_FILL_CEILING so a fresh deck never spends unbounded
   *  Vertex calls. Reset at the start of each turn. */
  private autoFillCount = 0;
  /** Consecutive "Vertex unconfigured" (503 / ErrNotConfigured) failures observed
   *  during the progressive fill; once it reaches AUTO_FILL_503_LIMIT the run
   *  short-circuits so a dead image backend isn't hammered for the rest of the
   *  deck. */
  private autoFill503s = 0;
  /** Set once the progressive fill has short-circuited (repeated Vertex-unconfigured
   *  errors) — stops any further enqueues for the rest of this run. */
  private autoFillStopped = false;
  /** The queue of empty slots awaiting progressive background generation, plus the
   *  bounded-worker bookkeeping (concurrency 3). Filled as each `slide` stream
   *  event lands; drained non-blocking so the (already-shown) slide just has its
   *  image pop in. */
  private autoFillQueue: { slideId: string; region: string }[] = [];
  /** How many progressive-fill workers are currently draining the queue (≤ 3). */
  private autoFillWorkers = 0;
  /** True for the duration of a FRESH first-generation turn — gates whether a
   *  `slide` stream event enqueues its empty slots. A refine turn (the deck
   *  already had slides) leaves this false so existing imagery is never touched. */
  private autoFillThisRun = false;
  /** Slide ids already edited by the post-generation correction pass THIS run.
   *  The structural self-check (Task 5) records the slides it tightened; the
   *  advisory visual-check pass (Task 6) reads this so it never double-corrects a
   *  slide Task 5 already touched, and records its own corrections too. Reset at
   *  the start of each check→correct pass. */
  private correctedThisRun = new Set<string>();
  @state() private chatW = Number(localStorage.getItem('rdx-decks-chatw')) || 420;
  @state() private dragFrom: number | null = null;
  @state() private saveState: 'idle' | 'saving' | 'saved' = 'idle';
  private saveTimer: ReturnType<typeof setTimeout> | undefined;
  // Inline-edit overrides (slideId -> regionId -> plain text). Applied by the
  // renderer everywhere — focused canvas, preview iframe, and the saved artifact.
  @state() private overrides: DeckOverrides = {};
  // Rich-text region styles (slideId -> regionId -> { h, align }). A parallel
  // channel to overrides — heading size + alignment applied by the renderer as
  // rt-* classes (not stored as tags, so they survive sanitisation). Threaded
  // through EVERY render entry point (focus canvas, thumbnails, present, persist,
  // live viewer) so a style edit round-trips, bakes, and reaches anonymous
  // viewers.
  @state() private regionStyles: DeckRegionStyles = {};
  // ── inline rich-text floating toolbar ──
  // Shown when the user has a non-collapsed selection inside a RICH [data-region]
  // that is currently being inline-edited. Carries the region id (so the
  // heading/align buttons write regionStyles for the WHOLE region) and the
  // selection rect (so the toolbar floats over the selection).
  @state() private rtBar: { region: string } | null = null;
  private rtRect: { left: number; top: number; bottom: number } | null = null;
  /** The colour-swatch sub-popover state for the rich toolbar. */
  @state() private rtColourOpen = false;
  /** Document-level selection listener, live only while an inline rich edit is
   *  in progress (added in beginInlineEdit, removed on finish). */
  private rtSelListener: (() => void) | null = null;
  /** Signature of the slide currently painted into the focus editor, so updated()
   *  only re-paints when it actually changes (not on every keystroke commit). */
  private focusKey = '';
  /** Slide id whose notes are currently mounted in the rich-text editor, so
   *  updated() populates the contenteditable once per slide (never mid-edit). */
  private notesKey = '';
  /** Last seen thread length, so updated() auto-scrolls the chat on new messages. */
  private lastThreadLen = 0;
  /** Last document title we set, so updated() only writes when the deck title
   *  actually changes (the studio re-renders on every keystroke). */
  private lastDocTitle = '';
  // ── per-message feedback (thumbs up / down) ───────────────────────────
  /** The chosen rating per assistant message, keyed by its stable messageRef
   *  (see msgRef). Optimistic local state so the chosen thumb stays highlighted
   *  and survives re-render within the session. */
  @state() private ratings: Record<string, MessageFeedbackRating> = {};
  /** The messageRef whose "what was wrong?" one-liner is currently open (after a
   *  thumbs-down). Only one note input is shown at a time. */
  @state() private feedbackNoteFor: string | null = null;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  private onEsc = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    // close the top-most layer only
    if (this.confirm) { this.confirm = null; }
    else if (this.genAllOpen) { this.genAllOpen = false; }
    else if (this.libOpen) { this.libOpen = false; }
    else if (this.diff) { this.diff = null; }
    else if (this.rtColourOpen) { this.rtColourOpen = false; }
    else if (this.rtBar) { this.rtBar = null; }
    else if (this.cardEdit) { this.cardEdit = false; }
    else if (this.imgSel) { this.imgSel = null; }
    else if (this.sel) { this.sel = null; }
    else if (this.toolMenu) { this.toolMenu = null; }
    else if (this.themeOpen) { this.themeOpen = false; }
    else if (this.brandOpen) { this.brandOpen = false; }
    else if (this.notesOpen) { this.notesOpen = false; }
    else return;
    e.stopPropagation();
  };

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onEsc);
    // Pick up any documents the Home composer attached ("Add a brief"); they are
    // uploaded as sources once the deck is first saved (flushPendingBriefs).
    try {
      const raw = sessionStorage.getItem('rdx-decks-briefs');
      if (raw) {
        this.pendingBriefs = JSON.parse(raw) as PendingBrief[];
        sessionStorage.removeItem('rdx-decks-briefs');
      }
    } catch { /* ignore */ }
    const params = new URLSearchParams(location.search);
    // Open an existing deck for view/edit (/studio?deck=<id>).
    const deckId = params.get('deck');
    if (deckId) { void this.loadDeck(deckId); return; }
    // Start from a Gallery template (stashed by the gallery's "Use" action).
    let tplId = '';
    try { tplId = sessionStorage.getItem('rdx-decks-template') || ''; if (tplId) sessionStorage.removeItem('rdx-decks-template'); } catch { /* ignore */ }
    if (tplId) { queueMicrotask(() => void this.openTemplate(tplId)); return; }
    // Accept an initial brief from the Home/Templates composer. Preferred via
    // sessionStorage ('rdx-decks-seed' — no length limit, not in the URL); the
    // legacy ?q= path is still honoured for back-compat.
    let seed = '';
    try {
      seed = sessionStorage.getItem('rdx-decks-seed') || '';
      if (seed) sessionStorage.removeItem('rdx-decks-seed');
    } catch { /* ignore */ }
    if (!seed) seed = params.get('q') || '';
    if (seed && this.thread.length === 0) {
      // defer so the first render mounts before the request fires
      queueMicrotask(() => void this.send(seed));
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onEsc);
    this.teardownRichEdit(); // drop any stray selectionchange listener
  }

  /** Apply the deck's brand ink + surface tint at the document level (slides
   *  inherit them). The deck THEME/FONT are NOT set on <html> — each slide
   *  self-themes via data-theme/data-font on the .slide element, so the app
   *  chrome keeps the user's own light/dark choice (the nav-bar toggle owns
   *  <html data-theme>) while slides render in their deck/per-slide theme. */
  private applyDeckTheme(deck: SlideDeck): void {
    // Coherence guard: a generated/loaded deck whose model left meta.theme unset
    // or invalid (or omitted meta.brand entirely — the V2 model frequently does)
    // would otherwise paint with data-theme falling back to workplace-paper while
    // applyDeckTheme set no accent, leaving the chrome/text out of step with the
    // scheme until the user opened the Theme menu. Normalise the model HERE — the
    // single apply point every entry uses (load / outline / envelope / bundle) —
    // so the first paint is coherent and the saved model carries the resolved
    // theme + brand. Only fills a MISSING/invalid value; a valid choice is kept.
    const validTheme = !!deck.meta.theme && THEME_IDS.includes(deck.meta.theme);
    if (!validTheme) deck.meta.theme = 'workplace-paper';
    const hexish = (v?: string) => !!v && /^#[0-9a-fA-F]{3,8}$/.test(v);
    // No accent at all (no brandKit.accent, no meta.brand) → adopt the chosen
    // bundle's accent (Paper's is the RDx house green #00A76F) so highlights read
    // on-brand from the first frame rather than inheriting a stale prior accent.
    if (!hexish(deck.meta.brandKit?.accent) && !hexish(deck.meta.brand)) {
      deck.meta.brand = bundleFor(deck.meta.theme).accent;
    }
    // brandKit.accent (when valid) wins over meta.brand — mirrors the renderer's
    // brandStyleVars precedence so the live canvas matches the artifact.
    const root = document.documentElement.style;
    const hex = (v?: string) => !!v && /^#[0-9a-fA-F]{3,8}$/.test(v);
    // Accent (highlights only — never the background).
    const accent = deck.meta.brandKit?.accent || deck.meta.brand;
    if (hex(accent)) root.setProperty('--brand-color', accent as string);
    // Manual text/ink colour (else cleared, so auto-contrast / theme text wins).
    const text = deck.meta.brandKit?.text;
    if (hex(text)) {
      root.setProperty('--deck-text', text as string);
      root.setProperty('--deck-text-2', `color-mix(in srgb, ${text} 72%, transparent)`);
      root.setProperty('--deck-text-3', `color-mix(in srgb, ${text} 52%, transparent)`);
    } else {
      root.removeProperty('--deck-text'); root.removeProperty('--deck-text-2'); root.removeProperty('--deck-text-3');
    }
    // Background (slide canvas) colour override (else the theme canvas).
    const bg = deck.meta.brandKit?.bg;
    if (hex(bg)) root.setProperty('--deck-bg', bg as string); else root.removeProperty('--deck-bg');
  }

  /** Apply a whole theme bundle — scheme + font + accent together (the hybrid
   *  control; each axis is still individually overridable below). */
  private applyBundle(id: string): void {
    if (!this.deck) return;
    const b = bundleFor(id);
    this.deck = { ...this.deck, meta: { ...this.deck.meta, theme: b.id, fontset: b.fontset, brand: b.accent } };
    try { localStorage.setItem('rdx-decks-ink', b.accent); } catch { /* ignore */ }
    this.applyDeckTheme(this.deck);
    this.focusKey = ''; // repaint the focus slide so its baked-in self-theme updates
    void this.persist(true);
  }
  /** Override just the font axis. */
  private setFontset(f: FontAxis): void {
    if (!this.deck) return;
    this.deck = { ...this.deck, meta: { ...this.deck.meta, fontset: f } };
    this.applyDeckTheme(this.deck);
    this.focusKey = '';
    void this.persist(true);
  }

  /** Effective theme/font for a slide preview host: the per-slide override, else
   *  the deck default. Used to self-theme every slide surface so a slide renders
   *  in its own theme while the app chrome keeps the user's light/dark choice. */
  private slideTheme(s?: Slide | null): string {
    const t = (s as { theme?: string } | null | undefined)?.theme;
    if (t && THEME_IDS.includes(t)) return t;
    const d = this.deck?.meta.theme;
    return d && THEME_IDS.includes(d) ? d : 'workplace-paper';
  }
  private slideFont(s?: Slide | null): FontAxis {
    return (s as { font?: FontAxis } | null | undefined)?.font || safeFont(this.deck?.meta.fontset);
  }
  /** Per-slide theme override (null clears it → the slide follows the deck). */
  private setSlideTheme(theme: string | null): void {
    if (this.focusIndex == null) return;
    this.mutateSlides((s) => {
      const c = { ...s[this.focusIndex!] } as Slide & { theme?: string };
      if (theme) c.theme = theme; else delete c.theme;
      s[this.focusIndex!] = c;
    });
    this.focusKey = '';
    this.toolMenu = null;
  }

  /** Count of slides carrying a per-slide theme override (≠ the deck theme). */
  private slideThemeOverrides(): number {
    return (this.deck?.slides || []).filter((s) => {
      const t = (s as { theme?: string }).theme;
      return !!t && THEME_IDS.includes(t);
    }).length;
  }

  /** Clear every per-slide theme override so the whole deck follows one theme —
   *  the one-click route back to a consistent look. */
  private clearAllSlideThemes(): void {
    this.mutateSlides((s) => {
      for (let i = 0; i < s.length; i++) {
        const t = (s[i] as { theme?: string }).theme;
        if (t) { const c = { ...s[i] } as Slide & { theme?: string }; delete c.theme; s[i] = c; }
      }
    });
    this.focusKey = '';
  }

  /** Deck-level theme + font menu — a bundle re-themes everything; font is an
   *  independent override (the hybrid model). */
  private renderThemeMenu(): TemplateResult {
    if (!this.deck) return html``;
    const curTheme = this.deck.meta.theme && THEME_IDS.includes(this.deck.meta.theme) ? this.deck.meta.theme : 'workplace-paper';
    const curFont = safeFont(this.deck.meta.fontset);
    const slide = this.focusSlide;
    const slideTheme = (slide as { theme?: string } | null)?.theme || '';
    return html`
      <div class="theme-pop" role="menu" aria-label="Theme and fonts">
        <div class="tp-label">Theme · whole deck</div>
        <div class="tp-hint faint">Sets the background, text and a starting accent for every slide. Fine-tune any of the three in Brand. Your app's light/dark mode is separate.</div>
        <div class="tp-bundles">
          ${THEME_BUNDLES.map((b) => html`
            <button class="tp-bundle ${curTheme === b.id ? 'on' : ''}" role="menuitemradio" aria-checked=${curTheme === b.id} title=${b.blurb} @click=${() => this.applyBundle(b.id)}>
              <span class="tp-chip ${b.dark ? 'is-dark' : 'is-light'}" style="--c:${b.accent}"></span>
              <span class="tp-name">${b.label}</span>
            </button>`)}
        </div>
        ${this.slideThemeOverrides() > 0 ? html`
          <div class="tp-override-note">
            <span class="faint">${this.slideThemeOverrides()} slide${this.slideThemeOverrides() === 1 ? '' : 's'} ${this.slideThemeOverrides() === 1 ? 'has' : 'have'} their own theme</span>
            <button class="tp-reset" @click=${() => this.clearAllSlideThemes()}>Reset all to deck</button>
          </div>` : nothing}
        <div class="tp-label">Font · whole deck</div>
        <div class="tp-fonts">
          ${FONT_AXES.map((f) => html`
            <button class="tp-font ${curFont === f.id ? 'on' : ''}" role="menuitemradio" aria-checked=${curFont === f.id} title=${f.blurb} @click=${() => this.setFontset(f.id)}>
              <span class="tp-font-name" style=${f.id === 'editorial' ? 'font-family:var(--font-serif)' : 'font-family:var(--font-sans)'}>${f.label}</span>
              <span class="tp-font-blurb faint">${f.blurb}</span>
            </button>`)}
        </div>
        ${slide ? html`
          <div class="tp-label">This slide only</div>
          <div class="tp-hint faint">Override the theme for the focused slide. The rest of the deck is unchanged.</div>
          <div class="tp-bundles">
            <button class="tp-bundle ${!slideTheme ? 'on' : ''}" role="menuitemradio" aria-checked=${!slideTheme} title="Follow the deck theme" @click=${() => this.setSlideTheme(null)}>
              <span class="tp-chip is-light" style="--c:var(--border-strong)"></span>
              <span class="tp-name">Follow deck</span>
            </button>
            ${THEME_BUNDLES.map((b) => html`
              <button class="tp-bundle ${slideTheme === b.id ? 'on' : ''}" role="menuitemradio" aria-checked=${slideTheme === b.id} title=${`This slide: ${b.label}`} @click=${() => this.setSlideTheme(b.id)}>
                <span class="tp-chip ${b.dark ? 'is-dark' : 'is-light'}" style="--c:${b.accent}"></span>
                <span class="tp-name">${b.label}</span>
              </button>`)}
          </div>` : nothing}
      </div>`;
  }

  private setAccent(hex: string): void {
    document.documentElement.style.setProperty('--brand-color', hex);
    try { localStorage.setItem('rdx-decks-ink', hex); } catch { /* ignore */ }
    if (this.deck) this.deck = { ...this.deck, meta: { ...this.deck.meta, brand: hex } };
    this.toolMenu = null;
    this.focusKey = ''; // repaint the focus slide so the accent updates
    this.scheduleSave();
  }

  // ── brand kit (customer branding) ─────────────────────────────────────
  /** Merge a partial brand kit into deck.meta.brandKit, persisting like the
   *  theme/accent controls. Keys set to undefined are dropped so the brand kit
   *  stays minimal (and a fully-cleared kit collapses to no brandKit at all, so
   *  the deck reverts to byte-identical default output). */
  private setBrand(patch: Partial<DeckBrand>): void {
    if (!this.deck) return;
    const next: DeckBrand = { ...(this.deck.meta.brandKit || {}), ...patch };
    for (const k of Object.keys(next) as (keyof DeckBrand)[]) {
      const v = next[k];
      if (v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v))) delete next[k];
    }
    const brandKit = Object.keys(next).length ? next : undefined;
    this.deck = { ...this.deck, meta: { ...this.deck.meta, brandKit } };
    this.applyDeckTheme(this.deck); // refresh accent / background / text vars live
    this.focusKey = ''; // repaint the focus slide (cover mark / tint) with the change
    this.scheduleSave();
  }

  /** Set the brand accent (the custom colour that wins over meta.brand). Mirrors
   *  setAccent so the live canvas + chrome reflect it immediately. */
  private setBrandAccent(hex: string): void {
    const h = hex.trim();
    if (h && !/^#[0-9a-fA-F]{3,8}$/.test(h)) return; // ignore an incomplete hex field
    document.documentElement.style.setProperty('--brand-color', h);
    try { if (h) localStorage.setItem('rdx-decks-ink', h); } catch { /* ignore */ }
    this.setBrand({ accent: h || undefined });
  }

  /** Set the background (slide canvas) colour. Empty/invalid clears it back to
   *  the theme canvas. Accent is never used for the background. */
  private setBrandBg(hex: string): void {
    const h = hex.trim();
    if (h && !/^#[0-9a-fA-F]{3,8}$/.test(h)) return; // ignore an incomplete hex field
    this.setBrand({ bg: h || undefined });
  }

  /** Toggle the "RICOH RDx × <customer>" co-brand lockup (default on). */
  private toggleCoBrand(): void {
    const cur = this.deck?.meta.brandKit?.coBrand !== false;
    // store false explicitly (so "off" survives); true is the default → drop it.
    this.setBrand({ coBrand: cur ? false : undefined });
  }

  /** Accept a pasted logo URL verbatim (the renderer's srcAttr allow-list drops
   *  anything that is not /, http(s):, or data:image/). */
  private setBrandLogoUrl(url: string): void {
    this.setBrand({ logo: url.trim() || undefined });
  }

  /** Upload a logo. The deck-source bucket has no public image URL to reference,
   *  so the logo is stored as a DOWNSCALED data-URL (auto-fit to ~160px tall) and
   *  embedded — keeping the exported artifact self-contained. A best-effort copy
   *  is also pushed to the source bucket for backup/grounding. */
  private async onBrandLogoUpload(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || !this.deck) return;
    this.error = '';
    if (file.size > 6 * 1024 * 1024) {
      this.error = `That logo is ${(file.size / 1048576).toFixed(1)} MB — please keep it under 6 MB.`;
      input.value = ''; return;
    }
    this.uploading = file.name;
    try {
      const dataUrl = await fileToDownscaledLogo(file);
      // best-effort backup to the deck's source bucket (the embedded data-URL is
      // the source of truth; api.uploadSource returns no usable image URL).
      if (this.deckId) {
        try {
          const b64 = dataUrl.split(',')[1] || '';
          await api.uploadSource(this.deckId, { filename: file.name, mime: 'image/png', contentBase64: b64 });
        } catch { /* the embedded data URL is authoritative; bucket is a bonus */ }
      }
      this.setBrand({ logo: dataUrl, name: this.deck.meta.brandKit?.name });
    } catch {
      this.error = 'Couldn’t read that logo image.';
    } finally {
      this.uploading = '';
      input.value = '';
    }
  }

  /** The Brand-kit panel: logo + customer name + the three independent colour
   *  axes (Accent / Background / Text), each "theme default" or a custom colour,
   *  plus the co-brand toggle. Accent is highlights only — never the background. */
  private renderBrandMenu(): TemplateResult {
    if (!this.deck) return html``;
    const bk = this.deck.meta.brandKit || {};
    const accent = (bk.accent || '').trim();
    const accentForPicker = /^#[0-9a-fA-F]{6}$/.test(accent) ? accent : '#00a76f';
    const bgForPicker = /^#[0-9a-fA-F]{6}$/.test(bk.bg || '') ? (bk.bg as string) : '#141416';
    const coBrand = bk.coBrand !== false;
    return html`
      <div class="theme-pop brand-pop" role="menu" aria-label="Brand kit">
        <div class="tp-label">Customer logo</div>
        <div class="brand-logo-row">
          ${bk.logo
            ? html`<span class="brand-logo-prev"><img src=${bk.logo} alt="Customer logo"></span>`
            : html`<span class="brand-logo-prev is-empty" aria-hidden="true">▤</span>`}
          <div class="brand-logo-ctl">
            <input class="brand-input" type="text" placeholder="Paste a logo URL…" .value=${bk.logo && !bk.logo.startsWith('data:') ? bk.logo : ''}
              @change=${(e: Event) => this.setBrandLogoUrl((e.target as HTMLInputElement).value)}>
            <label class="brand-upload-btn">
              <input type="file" hidden accept="image/png,image/jpeg,image/webp,image/avif,image/svg+xml" @change=${(e: Event) => this.onBrandLogoUpload(e)}>
              ${this.uploading ? 'Uploading…' : (bk.logo ? '⬆ Replace' : '⬆ Upload')}
            </label>
            ${bk.logo ? html`<button class="brand-clear" title="Remove the logo" @click=${() => this.setBrand({ logo: undefined })}>Remove</button>` : nothing}
          </div>
        </div>

        <div class="tp-label">Customer name</div>
        <input class="brand-input" type="text" placeholder="e.g. Acme Corp" .value=${bk.name || ''}
          @change=${(e: Event) => this.setBrand({ name: (e.target as HTMLInputElement).value || undefined })}>

        <label class="brand-toggle">
          <input type="checkbox" .checked=${coBrand} @change=${() => this.toggleCoBrand()}>
          <span>Co-brand “RICOH RDx × ${bk.name || 'customer'}”</span>
        </label>

        <div class="tp-label">Accent <span class="faint">· highlights</span></div>
        <div class="tp-hint faint">Eyebrows, key figures, bars and links — not the background.</div>
        <div class="accent-swatches">
          ${ACCENTS.map((a) => html`<button class="accent-sw ${accent.toLowerCase() === a.hex.toLowerCase() ? 'on' : ''}" style="background:${a.hex}" title=${a.name} aria-label=${a.name} @click=${() => this.setBrandAccent(a.hex)}></button>`)}
        </div>
        <div class="brand-accent-free">
          <input type="color" aria-label="Custom accent colour" .value=${accentForPicker} @input=${(e: Event) => this.setBrandAccent((e.target as HTMLInputElement).value)}>
          <input class="brand-input brand-hex" type="text" placeholder="#RRGGBB" .value=${accent}
            @change=${(e: Event) => this.setBrandAccent((e.target as HTMLInputElement).value)}>
        </div>

        <div class="tp-label">Background <span class="faint">· slide canvas</span></div>
        <div class="tp-hint faint">The whole-slide background colour. Default follows the theme.</div>
        <div class="brand-accent-free">
          <input type="color" aria-label="Custom background colour" .value=${bgForPicker} @input=${(e: Event) => this.setBrandBg((e.target as HTMLInputElement).value)}>
          <input class="brand-input brand-hex" type="text" placeholder="Theme default" .value=${bk.bg || ''}
            @change=${(e: Event) => this.setBrandBg((e.target as HTMLInputElement).value)}>
          ${bk.bg ? html`<button class="brand-clear" title="Use the theme background" @click=${() => this.setBrand({ bg: undefined })}>Theme</button>` : nothing}
        </div>

        <div class="tp-label">Text <span class="faint">· ink</span></div>
        <div class="tp-hint faint">Titles and body. Auto reads each slide's background and stays legible.</div>
        <div class="brand-accent-free">
          <input type="color" aria-label="Custom text colour"
            .value=${/^#[0-9a-fA-F]{6}$/.test(bk.text || '') ? (bk.text as string) : '#1e2a3a'}
            @input=${(e: Event) => this.setBrand({ text: (e.target as HTMLInputElement).value })}>
          <input class="brand-input brand-hex" type="text" placeholder="Auto (dynamic)" .value=${bk.text || ''}
            @change=${(e: Event) => { const v = (e.target as HTMLInputElement).value.trim(); this.setBrand({ text: v || undefined }); }}>
          ${bk.text ? html`<button class="brand-clear" title="Back to automatic, background-aware text" @click=${() => this.setBrand({ text: undefined })}>Auto</button>` : nothing}
        </div>
      </div>`;
  }

  // ── autosave + save feedback ──────────────────────────────────────────
  /** Queue a quiet save 1.5s after the last change (once the deck exists). */
  private scheduleSave(): void {
    if (!this.deck) return;
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => { void this.persist(false, true); }, 1500);
  }

  // ── resumable chat persistence ────────────────────────────────────────
  private chatSaveTimer: ReturnType<typeof setTimeout> | undefined;
  /** Persist the chat thread shortly after a turn, so the user can leave and
   *  resume. Only once a deck exists and the thread has real user content. */
  private scheduleChatSave(): void {
    if (!this.deckId || !this.thread.some((m) => m.role === 'user')) return;
    clearTimeout(this.chatSaveTimer);
    const id = this.deckId;
    const snapshot = this.thread;
    this.chatSaveTimer = setTimeout(() => { void api.saveChat(id, snapshot).catch(() => { /* best-effort */ }); }, 1200);
  }
  /** Start a fresh chat for this deck (clears the saved thread). The deck itself
   *  is untouched; context is preserved via the deck summary on the next turn. */
  private async newChat(): Promise<void> {
    clearTimeout(this.chatSaveTimer);
    this.thread = this.deck
      ? [{ role: 'assistant', content: `Fresh chat on **${this.deck.meta.title || 'this deck'}**. I still know the deck — ask me to change anything, or click any text on a slide to edit it.`, phase: 'answer' }]
      : [];
    this.intakeSel = {}; this.intakeOther = {}; this.intakeDone = {};
    this.pendingSlideCount = undefined; this.pendingFraming = undefined;
    this.pending = new Set(); this.aiBusy = new Set();
    this.error = '';
    if (this.deckId) { try { await api.clearChat(this.deckId); } catch { /* best-effort */ } }
  }

  /** Load an existing deck into the canvas for viewing + editing. */
  private async loadDeck(id: string): Promise<void> {
    this.loading = true;
    try {
      const deck = await api.getDeck(id);
      this.deckId = deck.id;
      const model = deck.model as unknown as (SlideDeck & { overrides?: Record<string, Record<string, string>>; regionStyles?: DeckRegionStyles }) | undefined;
      if (model && model.schemaVersion === 2 && Array.isArray(model.slides)) {
        // Lift any persisted inline-edit overrides + rich-text region styles back
        // out of the model so the editor re-applies them (the model stays a clean
        // SlideDeck for the renderer; these are re-folded in on persist()).
        const { overrides, regionStyles, ...clean } = model;
        // Coerce every loaded slide so a PARTIAL slide (one saved during a past
        // generation error without its required array) is repaired to a coherent
        // shape before it reaches the renderer — otherwise it throws on render and
        // breaks the studio canvas, blocking ALL editing of the deck. normaliseSlide
        // is additive + idempotent, so complete slides are untouched.
        const cleanDeck = clean as SlideDeck;
        this.deck = { ...cleanDeck, slides: (cleanDeck.slides || []).map((sl) => normaliseSlide(sl)) };
        this.overrides = overrides && typeof overrides === 'object' ? overrides : {};
        this.regionStyles = regionStyles && typeof regionStyles === 'object' ? regionStyles : {};
        this.applyDeckTheme(this.deck);
        this.focusIndex = 0;
        // Resume the saved chat if there is one; otherwise greet.
        const greeting: ThreadMsg = {
          role: 'assistant',
          content: model.slides.length
            ? `Opened **${deck.title}** — ${model.slides.length} slides. Ask me to change anything, or click any text on a slide to edit it.`
            : `Picking up your draft. Tell me what you'd like, or describe the deck and I'll build it.`,
          phase: 'answer',
        };
        try {
          const saved = await api.getChat(id);
          const msgs = (saved.messages || []) as ThreadMsg[];
          this.thread = msgs.length ? msgs : [greeting];
        } catch { this.thread = [greeting]; }
        try { this.sources = await api.listSources(id); } catch { /* ignore */ }
      } else {
        this.error = 'This deck has no editable V2 model yet — open it in the classic viewer.';
      }
    } catch (e) {
      this.error = e instanceof HttpError ? `Could not open deck (${e.status}).` : 'Could not open deck.';
    } finally {
      this.loading = false;
    }
  }

  /** Ensure a persisted deck exists BEFORE the first turn, so: (1) the chat is
   *  saved against a real deck id and survives a refresh even before any slides
   *  are generated, and (2) attached briefs upload as sources so the very first
   *  generation is grounded in them. The deck starts empty (no slides) — the
   *  canvas keeps showing "your deck will appear here" until slides arrive. */
  private async ensureDeck(): Promise<void> {
    if (this.deckId) return;
    if (!this.deck) {
      let ink = '#00A76F';
      try { ink = localStorage.getItem('rdx-decks-ink') || ink; } catch { /* ignore */ }
      this.deck = { schemaVersion: 2, meta: { title: 'Untitled deck', brand: ink }, slides: [] };
    }
    const ok = await this.persist(true);
    if (ok && this.deckId) {
      // Put the deck id in the URL so a refresh reopens this deck + its chat.
      try { history.replaceState(null, '', `/studio?deck=${encodeURIComponent(this.deckId)}`); } catch { /* ignore */ }
      await this.flushPendingBriefs();
    }
  }

  /** Start a new deck from a Gallery template (its "Use" action). */
  private async openTemplate(tid: string): Promise<void> {
    let tpl: LibTemplate | undefined;
    for (const g of SLIDE_LIBRARY) { const t = g.items.find((x) => x.id === tid); if (t) { tpl = t; break; } }
    if (!tpl) return;
    const slide = JSON.parse(JSON.stringify(tpl.slide)) as Slide;
    slide.id = this.newSlideId(slide.kind);
    // honour a layout chosen in the gallery preview, validated against the kind
    try {
      const lay = sessionStorage.getItem('rdx-decks-template-layout') || '';
      sessionStorage.removeItem('rdx-decks-template-layout');
      if (lay && KIND_META[slide.kind].layouts.some((l) => l.id === lay)) {
        (slide as { layout?: string }).layout = lay;
      }
    } catch { /* ignore */ }
    let ink = '#00A76F';
    try { ink = localStorage.getItem('rdx-decks-ink') || ink; } catch { /* ignore */ }
    this.deck = { schemaVersion: 2, meta: { title: `${tpl.name} deck`, brand: ink }, slides: [slide] };
    this.focusIndex = 0;
    this.thread = [{ role: 'assistant', content: `Started from the **${tpl.name}** template. Ask me to change anything, or click any text on the slide to edit it.`, phase: 'answer' }];
    const ok = await this.persist(true);
    if (ok && this.deckId) { try { history.replaceState(null, '', `/studio?deck=${encodeURIComponent(this.deckId)}`); } catch { /* ignore */ } }
  }

  /** Upload any Home-attached documents once we have a saved deck to hang them
   *  off. Best-effort: failures leave them pending. */
  private async flushPendingBriefs(): Promise<void> {
    if (!this.deckId || this.pendingBriefs.length === 0) return;
    const queue = this.pendingBriefs;
    this.pendingBriefs = [];
    for (const b of queue) {
      try {
        const src = await api.uploadSource(this.deckId, { filename: b.name, mime: b.mime, contentBase64: b.b64 });
        this.sources = [src, ...this.sources];
      } catch { /* drop on failure */ }
    }
  }

  /** The chat turns sent to the server (user/assistant content only). */
  private chatTurns(): ChatTurn[] {
    return this.thread.map((m) => ({ role: m.role, content: m.content }));
  }

  /** Tracks whether the current streamed turn fanned out to per-slide
   *  sub-agents (so "done" can post the summary message). */
  private streamFannedOut = false;
  /** Aborts the in-flight streamed turn when the user hits Stop. */
  private abort: AbortController | null = null;

  private async send(text: string): Promise<void> {
    const msg = text.trim();
    if (!msg || this.loading) return;
    this.thread = [...this.thread, { role: 'user', content: msg }];
    this.input = '';
    await this.runTurn();
  }

  /** Re-answer the last user message: drop any trailing assistant turns, then
   *  re-run. Useful when a reply wasn't quite right. */
  private async regenerate(): Promise<void> {
    if (this.loading) return;
    const th = [...this.thread];
    while (th.length && th[th.length - 1].role === 'assistant') th.pop();
    if (!th.some((m) => m.role === 'user')) return;
    this.thread = th;
    await this.runTurn();
  }

  /** Stop the current streamed turn. */
  private stop(): void { this.abort?.abort(); }

  /** Run one co-pilot turn over the current thread (shared by send + regenerate). */
  private async runTurn(): Promise<void> {
    this.error = '';
    this.loading = true;
    this.streamFannedOut = false;
    this.abort = new AbortController();
    // Keep a local handle to THIS turn's controller — the finally block nulls
    // this.abort, so the post-stream self-check reads its aborted state from here.
    const ac = this.abort;
    // Was this a FIRST generation? Captured BEFORE ensureDeck (which mints an
    // empty 0-slide draft) so it reflects the pre-turn slide count — used to gate
    // the progressive per-slide image fill (a refine turn is never empty, so its
    // already-imaged slides are left alone). Reset the per-run fill bookkeeping
    // here so each turn starts from a clean ceiling / short-circuit state.
    const wasEmpty = !this.deck || this.deck.slides.length === 0;
    this.autoFillThisRun = wasEmpty;
    // A fresh generation drives the "Building your deck" animation; a refine turn
    // (the deck already has slides) does not — it keeps its per-slide shimmer.
    this.building = wasEmpty;
    // Reset the building captions so a previous turn's status / slide title never
    // bleeds into this one before the first stream event lands.
    this.buildingStatus = '';
    this.buildingTitle = '';
    this.autoFillCount = 0;
    this.autoFill503s = 0;
    this.autoFillStopped = false;
    // Create + persist an (empty) deck up front so the chat is durable and any
    // attached briefs ground the first generation.
    await this.ensureDeck();
    let turnFailed = false;
    try {
      await api.chatDeckStream({
        messages: this.chatTurns(),
        // Only send the deck as refine context once it actually has slides — an
        // empty draft must read as a fresh request, not a refine of nothing.
        currentModel: this.deck && this.deck.slides.length ? this.deck : undefined,
        deckId: this.deckId || undefined,
        focusedSlideId: this.focusSlide?.id,
        // Carry the chosen length / framing from the most recent intake submit, so
        // the fresh generation honours them. Forwarded only when set; cleared right
        // after this send so a later refine turn never resends stale values.
        slideCount: this.pendingSlideCount,
        framing: this.pendingFraming,
      }, (ev) => this.onStreamEvent(ev), this.abort.signal);
      this.pendingSlideCount = undefined;
      this.pendingFraming = undefined;
    } catch (e) {
      turnFailed = true;
      if (this.abort?.signal.aborted) {
        this.thread = [...this.thread, { role: 'assistant', content: '_Stopped._', phase: 'answer' }];
      } else {
        this.error = e instanceof HttpError ? `Chat failed (${e.status}).` : 'Chat failed.';
      }
    } finally {
      this.loading = false;
      this.building = false; // the finished (or failed/stopped) deck takes the canvas
      this.abort = null;
      this.scrollThread();
      this.scheduleChatSave();
    }
    // Imagery now fills PROGRESSIVELY, per slide, as each `slide` stream event
    // lands (see onStreamEvent → enqueueSlideImages) — no post-stream bulk pass.
    // A failed turn closes the run so no more slots are enqueued.
    if (turnFailed) this.autoFillThisRun = false;
    // Structural self-check (Task 5): after a SUCCESSFUL FRESH generation, run a
    // bounded render→check→correct pass — tighten overpacked slides, retry any
    // image slot that stayed empty after the fill pass, flag duplicate layouts.
    // Skipped on a refine turn (wasEmpty false) and on a stopped/failed turn.
    if (!turnFailed && wasEmpty && !ac.signal.aborted) {
      await this.checkAndCorrect(ac.signal);
    }
  }

  /** Handle one streamed event: the multi-slide plan, a per-slide sub-agent edit
   *  landing, the fallback envelope, or done/error. Slides patch + shimmer live. */
  private onStreamEvent(ev: StreamEvent): void {
    switch (ev.type) {
      case 'status':
        // A coarse backend phase caption for the PRE-outline building animation
        // ("Reading your document…" / "Researching…" / "Planning the outline…").
        // Keeps the canvas caption tracking real work rather than freezing. The
        // count-driven phase takes over once the outline lands (cleared below).
        this.buildingStatus = ev.message || '';
        break;
      case 'thinking':
        // The planner's live reasoning preamble (DISPLAY-ONLY) for the pre-outline
        // building phase. The server sends the accumulated text, so we set (not
        // append) it; it renders as a small growing block under the caption. Cleared
        // on 'outline' (the outline has landed) and on 'done'.
        if (this.building) this.buildingThinking = ev.message || '';
        break;
      case 'plan':
        this.streamFannedOut = true;
        this.aiBusy = new Set(ev.slideIds || []); // shimmer every target
        this.scrollThread();
        break;
      case 'outline':
        // The outline has landed — hand the caption to the count-driven phase by
        // clearing the server status (it only governs the pre-outline phase), and
        // drop the live thinking preamble (its job is done once the outline shows).
        this.buildingStatus = '';
        this.buildingThinking = '';
        // First-draft skeleton: build the deck shell, shimmer every slide as a
        // placeholder, then let each 'slide' event fill one in.
        if (ev.deck) {
          this.streamFannedOut = true;
          this.deck = ev.deck;
          this.applyDeckTheme(ev.deck);
          this.applyPreThemeChoice(); // the user's up-front theme pick beats the AI's
          const ids = ev.deck.slides.map((s) => s.id);
          this.pending = new Set(ids);
          this.aiBusy = new Set(ids);
          this.focusIndex = 0;
          this.focusKey = '';
        }
        break;
      case 'slide':
        if (ev.slide && ev.slideId && this.deck) {
          const id = ev.slideId;
          // Track the latest landed slide's title so the building animation's
          // streaming-phase detail reads "'<title>'" rather than "Slide N of M".
          if (this.building) this.buildingTitle = ev.slide.title || '';
          // Coerce the streamed slide before patching it in: a sub-agent edit that
          // lands without its required array (a generation error) is repaired to a
          // coherent shape so it renders degraded rather than throwing and breaking
          // the live canvas. normaliseSlide is additive + idempotent.
          const incoming = normaliseSlide(ev.slide as Slide);
          const known = this.deck.slides.some((s) => s.id === id);
          this.clearOverrides([id]); // the AI's new slide supersedes any inline edits
          if (known) {
            // Existing slide: patch it in place (edit / outline-fill / regen). It was
            // already shimmering from the plan/outline event — settle it now.
            this.deck = { ...this.deck, slides: this.deck.slides.map((s) => (s.id === id ? incoming : s)) };
            const busy = new Set(this.aiBusy); busy.delete(id); this.aiBusy = busy;
            if (this.pending.has(id)) { const p = new Set(this.pending); p.delete(id); this.pending = p; }
          } else {
            // Unknown id: the server is APPENDING a new slide (the add-slides path).
            // Append it rather than dropping it, and shimmer it until the run ends
            // (the 'done' handler clears aiBusy) so it reads as fresh AI content.
            // The add path emits no plan/outline event, so flag streamFannedOut here
            // too — that's what makes the 'done' handler post the summary, clear the
            // shimmer and save the appended slides.
            this.streamFannedOut = true;
            this.deck = { ...this.deck, slides: [...this.deck.slides, incoming] };
            const busy = new Set(this.aiBusy); busy.add(id); this.aiBusy = busy;
          }
          // Every outline slide has now streamed its content — hand the canvas
          // from the "Building your deck" animation over to the finished deck
          // (the last slides keep their per-slide shimmer until the run ends).
          if (this.building && this.pending.size === 0) this.building = false;
          this.focusKey = ''; // repaint the focus editor if it's this slide
          // Now that the slide is shown, kick off background image generation for
          // its EMPTY image-capable slots. On a FRESH generation enqueueSlideImages
          // does this under its own gate; an APPENDED slide is new content the user
          // explicitly asked for, so image it regardless of that fresh-gen gate.
          if (known) {
            this.enqueueSlideImages(id);
          } else {
            this.enqueueAddedSlideImages(id);
          }
        }
        break;
      case 'envelope':
        if (ev.envelope) void this.applyEnvelope(ev.envelope);
        break;
      case 'done':
        // The turn is finished — drop the building captions so a later turn never
        // shows a stale status / title / thinking preamble.
        this.buildingStatus = '';
        this.buildingTitle = '';
        this.buildingThinking = '';
        if (this.streamFannedOut) {
          this.thread = [...this.thread, { role: 'assistant', content: ev.message || 'Updated the slides.', phase: 'refine' }];
          this.pending = new Set();
          setTimeout(() => { this.aiBusy = new Set(); }, 700);
          this.scheduleSave();
        }
        break;
      case 'error':
        this.error = ev.message || 'Chat failed.';
        break;
      default:
        // Unknown event type (a future server addition) — ignore it rather than
        // letting the switch fall through silently with no handler. No-op.
        break;
    }
  }

  /** Apply a non-streamed (fallback) envelope: the assistant turn, any new deck,
   *  and a shimmer over the slides it reported changing. */
  private async applyEnvelope(env: ChatEnvelope): Promise<void> {
    this.thread = [...this.thread, {
      role: 'assistant', content: env.message || '', widgets: env.widgets || [], phase: env.phase,
    }];
    if (env.deck) {
      this.deck = env.deck;
      // Apply the deck's theme + brand to the chrome/CSS vars on first paint —
      // the streamed outline path does this, but the non-streamed envelope path
      // previously did not, so a fresh deck inherited the PRIOR deck's --deck-text
      // / --brand-color and rendered with the wrong text colour until the user
      // opened the Theme menu. applyDeckTheme also normalises a missing/invalid
      // meta.theme to workplace-paper so the scheme + vars are coherent.
      this.applyDeckTheme(this.deck);
      this.applyPreThemeChoice(); // the user's up-front theme pick beats the AI's
      this.focusKey = ''; // repaint the focus slide so its baked theme is current
      if (this.focusIndex == null) this.focusIndex = 0;
      const edited = (env.editedSlideIds || []).filter((id) => this.deck!.slides.some((s) => s.id === id));
      this.clearOverrides(edited); // the AI's edits supersede any stale inline overrides
      if (edited.length) {
        this.aiBusy = new Set(edited);
        setTimeout(() => { this.aiBusy = new Set(); }, 1800);
      }
      if (this.pendingBriefs.length && !this.deckId) {
        await this.persist(true);
        await this.flushPendingBriefs();
      }
      this.scheduleSave();
    }
  }

  private scrollThread(): void {
    this.updateComplete.then(() => {
      const t = this.querySelector('.chat-thread');
      if (t) t.scrollTop = t.scrollHeight;
    });
  }

  private onKey(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void this.send(this.input);
    }
  }

  // ── uploads ───────────────────────────────────────────────────────────
  private async onUpload(e: Event): Promise<void> {
    const fileInput = e.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file) return;
    this.error = '';
    // Reject oversize client-side with a clear message (the API caps at 8 MiB).
    if (file.size > 8 * 1024 * 1024) {
      this.error = `“${file.name}” is ${(file.size / 1048576).toFixed(1)} MB — the limit is 8 MB.`;
      fileInput.value = '';
      return;
    }
    // Uploads attach to a saved deck. Before the first generation there is no
    // deck yet (this.deck is null, so a bare persist() bails) — mint the instant
    // draft via ensureDeck() so a document can ground the very first generation.
    if (!this.deckId) {
      await this.ensureDeck();
      if (!this.deckId) { this.error = 'Could not start a deck to attach documents — try again.'; fileInput.value = ''; return; }
    }
    this.uploading = file.name; // shows the spinner + an "uploading" chip
    try {
      const b64 = await fileToBase64(file);
      const src = await api.uploadSource(this.deckId, {
        filename: file.name, mime: file.type || 'application/octet-stream', contentBase64: b64,
      });
      this.sources = [src, ...this.sources];
    } catch (err) {
      this.error = err instanceof HttpError
        ? (err.status === 413 ? `“${file.name}” is too large to upload.` : `Couldn’t add “${file.name}” (${err.status}). ${err.message || ''}`.trim())
        : `Couldn’t add “${file.name}”.`;
    } finally {
      this.uploading = '';
      fileInput.value = '';
    }
  }

  private async removeSource(id: string): Promise<void> {
    try {
      await api.deleteSource(this.deckId, id);
      this.sources = this.sources.filter((s) => s.id !== id);
    } catch { /* surface nothing; best-effort */ }
  }

  // ── persistence + present ─────────────────────────────────────────────
  private async persist(draft = false, quiet = false): Promise<boolean> {
    if (!this.deck) return false;
    clearTimeout(this.saveTimer);
    this.saving = true;
    this.saveState = 'saving';
    try {
      // Bake BOTH inline-edit overrides AND rich-text region styles into the
      // stored HTML (so the rt-* classes ride the artifact to anonymous viewers).
      const htmlDoc = renderSlidesDoc(this.deck, { overrides: this.overrides, regionStyles: this.regionStyles });
      const notes = this.deck.notes || {};
      const speakerNotes = this.deck.slides.map((s) => notes[s.id] || '');
      // Persist the inline-edit overrides + region styles WITH the model —
      // otherwise a saved edit lives only in the baked HTML and is lost when the
      // editor reloads from the model. Drop each key entirely when empty so
      // default decks stay byte-identical.
      const hasOverrides = Object.values(this.overrides).some((m) => m && Object.keys(m).length);
      const hasRegionStyles = Object.values(this.regionStyles).some((m) => m && Object.keys(m).length);
      const model = {
        ...this.deck,
        ...(hasOverrides ? { overrides: this.overrides } : {}),
        ...(hasRegionStyles ? { regionStyles: this.regionStyles } : {}),
      };
      const body = {
        title: this.deck.meta.title || 'Untitled deck',
        subtitle: this.deck.meta.subtitle,
        html: htmlDoc,
        speakerNotes,
        model: model as unknown as DeckModel, // server stores model opaque
        status: draft ? ('draft' as const) : ('saved' as const),
      };
      if (this.deckId) {
        await api.updateDeck(this.deckId, { id: this.deckId, ...body });
      } else {
        const created = await api.createDeck(body);
        this.deckId = created.id;
      }
      this.saveState = 'saved';
      setTimeout(() => { if (this.saveState === 'saved') this.saveState = 'idle'; }, 2200);
      return true;
    } catch (e) {
      this.saveState = 'idle';
      if (!quiet) this.error = e instanceof HttpError ? `Save failed (${e.status}).` : 'Save failed.';
      return false;
    } finally {
      this.saving = false;
    }
  }

  private async share(): Promise<void> {
    if (!this.deck || this.sharing) return;
    this.sharing = true;
    try {
      if (!this.deckId) {
        const ok = await this.persist(false);
        if (!ok) return;
      } else {
        await this.persist(false); // save latest edits before sharing
      }
      const s = await api.createShare(this.deckId, '30d');
      this.shareUrl = `${location.origin}/share/${s.token}`;
    } catch (e) {
      this.error = e instanceof HttpError ? `Share failed (${e.status}).` : 'Share failed.';
    } finally {
      this.sharing = false;
    }
  }

  private async copyShare(): Promise<void> {
    try { await navigator.clipboard.writeText(this.shareUrl); } catch { /* clipboard blocked */ }
  }

  private liveOverlay: (HTMLElement & { pushPosition(p: { slideId: string | null; step: number }): void }) | null = null;
  private livePos: { slideId: string | null; step: number } | null = null;

  private present(): void {
    if (!this.deck) return;
    // In-app full-screen presenter (the same slide-stage runtime the artifact
    // uses). A transient <template> feeds the slides (with inline edits applied);
    // onExit tears the overlay down.
    const stage = (window as unknown as { SlideStage?: {
      Present(root: Element, opts: Record<string, unknown>): { destroy(): void };
    } }).SlideStage;
    if (!stage) return;
    const tplId = 'studio-present-slides';
    document.getElementById(tplId)?.remove();
    const tpl = document.createElement('template');
    tpl.id = tplId;
    tpl.innerHTML = renderSlides(this.deck, this.overrides, this.regionStyles);
    document.body.appendChild(tpl);
    const root = document.createElement('div');
    document.body.appendChild(root);
    const cleanup = () => {
      ctl.destroy(); root.remove(); tpl.remove();
      // Exit/Esc also ends any live session: removing the overlay fires its
      // disconnectedCallback → setState(ended), so viewers don't hang.
      this.liveOverlay?.remove();
      this.liveOverlay = null;
    };
    const live = import.meta.env.VITE_LIVE_PRESENTATION === '1';
    const ctl = stage.Present(root, {
      template: tplId,
      notes: this.deck.notes || {},
      title: this.deck.meta.title,
      onExit: cleanup,
      // Relay every slide move to the live session (if one is running). The
      // overlay buffers until its session is created.
      onPosition: (p: { slideId: string | null; step: number }) => {
        this.livePos = p;
        this.liveOverlay?.pushPosition(p);
      },
      // "Go live" (flag-gated): save edits, then mount the live overlay ON TOP
      // of this Present view — the presenter keeps presenting; the overlay owns
      // the session and shows the share link + live "N watching" + Q&A. No
      // navigation, no app chrome.
      onGoLive: live
        ? () => {
            void this.persist(false).then((ok) => {
              if (!ok || !this.deckId || this.liveOverlay) return;
              const el = document.createElement('live-present-overlay') as
                HTMLElement & { pushPosition(p: { slideId: string | null; step: number }): void };
              el.setAttribute('deck-id', this.deckId);
              el.setAttribute('deck-title', this.deck?.meta.title ?? '');
              el.addEventListener('live-end', () => { el.remove(); this.liveOverlay = null; });
              document.body.appendChild(el);
              this.liveOverlay = el;
              if (this.livePos) el.pushPosition(this.livePos);
            });
          }
        : undefined,
    });
  }

  // ── structural slide editing ──────────────────────────────────────────
  private mutateSlides(fn: (slides: SlideDeck['slides']) => void): void {
    if (!this.deck) return;
    const slides = [...this.deck.slides];
    fn(slides);
    this.deck = { ...this.deck, slides };
    this.focusKey = ''; // force the focus editor to repaint
    this.scheduleSave();
  }
  private newSlideId(kind: string): string {
    return `s-${kind}-${Math.random().toString(36).slice(2, 8)}`;
  }
  private dupSlide(i: number): void {
    this.mutateSlides((s) => {
      const copy = JSON.parse(JSON.stringify(s[i])) as SlideDeck['slides'][number];
      copy.id = this.newSlideId(copy.kind);
      s.splice(i + 1, 0, copy);
    });
    this.focusIndex = i + 1;
  }
  private delSlide(i: number): void {
    const label = this.deck?.slides[i]?.title || this.deck?.slides[i]?.kind || 'this slide';
    this.confirm = {
      msg: `Delete “${label}”? This can’t be undone.`,
      onYes: () => {
        this.mutateSlides((s) => { s.splice(i, 1); });
        const n = this.deck?.slides.length ?? 0;
        this.focusIndex = n === 0 ? null : Math.min(i, n - 1);
        this.confirm = null;
      },
    };
  }

  // Live drag-reorder: the cards SWAP as the pointer moves over a new slot, so
  // it is always clear where the slide will land. Used by both the rail and the
  // grid; dragFrom tracks the moving slide's current index (it travels with it).
  private dragSwap(to: number): void {
    const from = this.dragFrom;
    if (from == null || from === to || !this.deck || to < 0 || to >= this.deck.slides.length) return;
    this.mutateSlides((s) => { const [m] = s.splice(from, 1); s.splice(to, 0, m); });
    if (this.focusIndex === from) this.focusIndex = to; // keep the focused slide selected
    this.dragFrom = to;
  }
  private endDrag(): void { this.dragFrom = null; }

  // drag the chat | canvas divider
  private startResize(e: MouseEvent): void {
    e.preventDefault();
    const ws = this.querySelector('.workspace') as HTMLElement | null;
    const left = ws ? ws.getBoundingClientRect().left : 0;
    const move = (ev: MouseEvent) => {
      this.chatW = Math.max(320, Math.min(ev.clientX - left, 760));
    };
    const up = () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.body.style.userSelect = '';
      try { localStorage.setItem('rdx-decks-chatw', String(this.chatW)); } catch { /* ignore */ }
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  }

  private get focusSlide(): Slide | null {
    return this.deck && this.focusIndex != null ? this.deck.slides[this.focusIndex] ?? null : null;
  }

  private setVariant(v: string): void {
    if (this.focusIndex == null) return;
    this.mutateSlides((s) => { s[this.focusIndex!] = { ...s[this.focusIndex!], variant: v } as Slide; });
    this.toolMenu = null;
  }

  /** Set the named layout (geometry) for the focused slide. */
  private setLayout(id: string): void {
    if (this.focusIndex == null) return;
    this.mutateSlides((s) => { s[this.focusIndex!] = { ...s[this.focusIndex!], layout: id } as Slide; });
    this.toolMenu = null;
  }

  /** Toggle the slide title on/off (the show.title flag). */
  private toggleTitle(): void {
    if (this.focusIndex == null) return;
    this.mutateSlides((s) => {
      const cur = s[this.focusIndex!] as Slide & { show?: { title?: boolean } };
      const on = cur.show?.title !== false;
      cur.show = { ...(cur.show || {}), title: !on };
    });
  }

  /** Title toggle applies to the kinds whose title flows through titleBlock
   *  (the hand-written-title kinds keep their title essential). */
  private titleToggleable(): boolean {
    const k = this.focusSlide?.kind;
    return !!k && !['cover', 'cta', 'full-bleed', 'quote-image', 'split'].includes(k);
  }
  private titleShown(): boolean {
    return (this.focusSlide as { show?: { title?: boolean } } | null)?.show?.title !== false;
  }

  /** The focused slide's effective layout id (raw field, else the legacy
   *  coverStyle, else the kind's first catalogue layout) — for picker highlight. */
  private curLayout(s: Slide): string {
    const allowed = KIND_META[s.kind].layouts.map((l) => l.id);
    const raw = (s as { layout?: string }).layout;
    if (raw && allowed.includes(raw)) return raw;
    const cs = (s as { coverStyle?: string }).coverStyle;
    if (cs && allowed.includes(cs)) return cs;
    return allowed[0];
  }

  private insertLibrary(t: LibTemplate): void {
    const tpl = JSON.parse(JSON.stringify(t.slide)) as Slide;
    if (this.libMode === 'replace' && this.focusIndex != null && this.deck) {
      // Change the slide TYPE: adopt the template but keep the slide's identity
      // and any shared header text the user already wrote.
      const cur = this.deck.slides[this.focusIndex];
      const merged = { ...tpl, id: cur.id, label: cur.label } as Slide;
      if ('title' in cur && cur.title && 'title' in merged) (merged as { title?: string }).title = cur.title;
      if ('eyebrow' in cur && cur.eyebrow && 'eyebrow' in merged) (merged as { eyebrow?: string }).eyebrow = cur.eyebrow;
      this.mutateSlides((s) => { s[this.focusIndex!] = merged; });
    } else {
      tpl.id = this.newSlideId(tpl.kind);
      const at = this.focusIndex != null ? this.focusIndex + 1 : (this.deck?.slides.length ?? 0);
      this.mutateSlides((s) => { s.splice(at, 0, tpl); });
      this.focusIndex = at;
    }
    this.libOpen = false;
  }

  // region popover → text edit
  private startTextEdit(): void {
    if (!this.sel) return;
    const region = this.sel.region;
    this.sel = null;
    this.beginInlineEdit(region);
  }

  /** Put a region into contenteditable. The override is committed LIVE (on every
   *  input), not only on blur, so a re-render mid-edit — e.g. changing the layout
   *  while typing — can never drop the in-progress text back to its default.
   *
   *  For a RICH region (title/lede/quote/eyebrow/sub/bullet-*) the committed value
   *  is `sanitizeRich(node.innerHTML)` — so inline formatting (bold/italic/
   *  underline/strike/link/colour, applied via the floating toolbar) is kept,
   *  while the render-side allowlist neutralises anything execCommand/paste emits
   *  outside the safe set. A non-rich region keeps the plain `innerText` contract
   *  exactly as before. The rich path also wires the selection toolbar. */
  private beginInlineEdit(region: string): void {
    if (this.focusIndex == null) return;
    const node = this.querySelector<HTMLElement>(`.focus-host [data-region="${region}"]`);
    if (!node || node.isContentEditable) return;
    const slide = this.focusSlide!;
    const rich = isRichRegion(region);
    node.setAttribute('contenteditable', 'true');
    node.classList.add('region--live');
    node.focus();
    const range = document.createRange(); range.selectNodeContents(node);
    const s = window.getSelection(); s?.removeAllRanges(); s?.addRange(range);
    const commit = () => {
      // Rich regions keep sanitised markup; non-rich keep trimmed plain text.
      const value = rich ? sanitizeRich(node.innerHTML) : (node.innerText || '').trim();
      this.overrides = { ...this.overrides, [slide.id]: { ...(this.overrides[slide.id] || {}), [region]: value } };
    };
    // For a rich region, reflect the live selection into the floating toolbar.
    const onSel = rich ? () => this.updateRichToolbar(node, region) : null;
    const finish = () => {
      node.removeAttribute('contenteditable');
      node.classList.remove('region--live');
      node.removeEventListener('blur', finish);
      node.removeEventListener('input', commit);
      if (onSel) {
        node.removeEventListener('mouseup', onSel);
        node.removeEventListener('keyup', onSel);
        document.removeEventListener('selectionchange', onSel);
        this.rtSelListener = null;
      }
      this.rtBar = null; this.rtColourOpen = false; // dismiss the toolbar
      commit();
      this.scheduleSave();
    };
    node.addEventListener('input', commit); // live commit — survives a re-render
    node.addEventListener('blur', finish);
    if (onSel) {
      node.addEventListener('mouseup', onSel);
      node.addEventListener('keyup', onSel);
      document.addEventListener('selectionchange', onSel);
      this.rtSelListener = onSel;
    }
    node.addEventListener('keydown', (ev: KeyboardEvent) => {
      // Esc dismisses the toolbar first; a second Esc (toolbar already closed)
      // blurs out of the edit.
      if (ev.key === 'Escape') {
        if (this.rtBar) { ev.preventDefault(); this.rtBar = null; this.rtColourOpen = false; return; }
        node.blur(); return;
      }
      // Enter commits + exits for non-rich (single-line) regions; in a rich
      // region Shift+Enter inserts a <br>, plain Enter still commits + exits.
      if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); node.blur(); }
    });
  }

  /** Detach the document-level selectionchange listener (added per rich edit) and
   *  hide the toolbar. Safe to call repeatedly; used when the focus host rebuilds
   *  (innerHTML swap kills the editable without a blur) and on disconnect. */
  private teardownRichEdit(): void {
    if (this.rtSelListener) {
      document.removeEventListener('selectionchange', this.rtSelListener);
      this.rtSelListener = null;
    }
    if (this.rtBar) this.rtBar = null;
    if (this.rtColourOpen) this.rtColourOpen = false;
  }

  /** Reflect the current selection into the floating rich-text toolbar: show it
   *  anchored over a non-collapsed selection inside `node`, hide it otherwise.
   *  Called on selectionchange / mouseup / keyup while a rich region is edited. */
  private updateRichToolbar(node: HTMLElement, region: string): void {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      if (this.rtBar) { this.rtBar = null; this.rtColourOpen = false; }
      return;
    }
    const range = sel.getRangeAt(0);
    // The selection must live inside the region being edited (ignore selections
    // that drifted into the toolbar or elsewhere).
    if (!node.contains(range.commonAncestorContainer)) {
      if (this.rtBar) { this.rtBar = null; this.rtColourOpen = false; }
      return;
    }
    const r = range.getBoundingClientRect();
    // A zero-size rect can occur transiently mid-drag — keep the last position.
    if (r.width || r.height) this.rtRect = { left: r.left, top: r.top, bottom: r.bottom };
    if (!this.rtBar || this.rtBar.region !== region) this.rtBar = { region };
    else this.requestUpdate(); // reposition on selection move
  }

  // region popover → Ask AI
  private async askRegion(instruction: string): Promise<void> {
    if (!this.sel || !this.focusSlide) return;
    const region = this.sel.region;
    const slide = this.focusSlide;
    const node = this.querySelector<HTMLElement>(`.focus-host [data-region="${region}"]`);
    const oldText = (node?.innerText || '').trim();
    this.sel = null; this.askVal = '';
    this.aiEditing = true;
    this.aiBusy = new Set([slide.id]); // shimmer the slide being worked
    try {
      const next = await api.editRegion(oldText, instruction);
      this.diff = { region, old: oldText, html: next };
      // hold the rect for positioning the diff popover
      if (node) { const r = node.getBoundingClientRect(); this.selRect = { left: r.left, top: r.top, bottom: r.bottom }; }
      void slide;
    } catch (e) {
      this.error = e instanceof HttpError ? `AI edit failed (${e.status}).` : 'AI edit failed.';
    } finally {
      this.aiEditing = false;
      this.aiBusy = new Set();
    }
  }

  private resolveDiff(accept: boolean): void {
    if (this.diff && accept && this.focusSlide) {
      const slide = this.focusSlide;
      this.overrides = { ...this.overrides, [slide.id]: { ...(this.overrides[slide.id] || {}), [this.diff.region]: this.diff.html } };
      this.focusKey = ''; // repaint the focus editor with the accepted text
      this.scheduleSave();
    }
    this.diff = null;
  }

  /** Set a rich-text region style (heading size / alignment) on the FOCUSED
   *  slide's region. A parallel channel to overrides — these apply as rt-*
   *  classes by the renderer (not stored as tags, so they survive sanitisation).
   *  Merges the patch over the region's current style, drops empty keys so an
   *  unstyled region collapses to nothing (default decks stay byte-identical),
   *  then repaints the focus canvas + schedules a save (mirrors mutateSlides). */
  private setRegionStyle(regionId: string, patch: Partial<RegionStyle>): void {
    const slide = this.focusSlide;
    if (!slide) return;
    const cur: RegionStyle = { ...(this.regionStyles[slide.id]?.[regionId] || {}), ...patch };
    // Normalise: drop an out-of-range/absent heading or alignment so the region
    // entry, and an empty slide entry, vanish rather than persist a no-op object.
    const next: RegionStyle = {};
    if (cur.h === 1 || cur.h === 2 || cur.h === 3) next.h = cur.h;
    if (cur.align === 'left' || cur.align === 'center' || cur.align === 'right') next.align = cur.align;
    const slideStyles = { ...(this.regionStyles[slide.id] || {}) };
    if (Object.keys(next).length) slideStyles[regionId] = next; else delete slideStyles[regionId];
    const all = { ...this.regionStyles };
    if (Object.keys(slideStyles).length) all[slide.id] = slideStyles; else delete all[slide.id];
    this.regionStyles = all;
    this.focusKey = ''; // repaint the focus canvas so the rt-* class change shows
    this.scheduleSave();
  }

  /** The current rich-text style for a region on the focused slide (for toolbar
   *  active-state highlighting). */
  private regionStyleOf(regionId: string): RegionStyle {
    const slide = this.focusSlide;
    return (slide && this.regionStyles[slide.id]?.[regionId]) || {};
  }

  /** Drop inline-edit overrides for slides the AI has just rewritten, so its new
   *  content isn't masked by a stale hand-edit on the same region. */
  private clearOverrides(ids: string[]): void {
    if (!ids.length || !Object.keys(this.overrides).length) return;
    const next = { ...this.overrides };
    let changed = false;
    for (const id of ids) { if (next[id]) { delete next[id]; changed = true; } }
    if (changed) this.overrides = next;
  }

  private setNote(text: string): void {
    if (!this.deck || !this.focusSlide) return;
    const notes = { ...(this.deck.notes || {}), [this.focusSlide.id]: text };
    this.deck = { ...this.deck, notes };
    this.scheduleSave();
  }

  // ── intake form ───────────────────────────────────────────────────────
  /** The state key for an intake question: its stable reserved `id` when present
   *  (so reserved-id questions like __length/__framing are addressable), falling
   *  back to the question TEXT for legacy widgets that ship no id. */
  private intakeKey(q: { id?: string; q: string }): string {
    return q.id || q.q;
  }
  /** A stable key for the whole widget's done-tracking — its question keys joined,
   *  so a widget re-renders as answered after its turn is sent. */
  private intakeWidgetKey(qs: { id?: string; q: string }[]): string {
    return qs.map((q) => this.intakeKey(q)).join('|');
  }
  private toggleIntake(qkey: string, opt: string): void {
    const cur = this.intakeSel[qkey] || [];
    const next = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
    this.intakeSel = { ...this.intakeSel, [qkey]: next };
  }
  /** Compose every answered question into one tidy message. */
  private composeIntake(qs: { id: string; q: string; options?: string[]; multi?: boolean }[]): string[] {
    const lines: string[] = [];
    for (const q of qs) {
      const key = this.intakeKey(q);
      const picks = [...(this.intakeSel[key] || [])];
      const other = (this.intakeOther[key] || '').trim();
      if (other) picks.push(other);
      if (picks.length) lines.push(`- ${q.q} ${picks.join(', ')}`);
    }
    return lines;
  }
  /** Read the reserved-id intake answers (`__length`, `__framing`) and stash them
   *  as pending fields the NEXT chat turn forwards. `__length` parses a selected
   *  "~6"/"~10"/"~15" option to its number (ignoring free text that isn't a
   *  number); `__framing` maps an "Overview…"/"Position…" option to the enum.
   *  Absent reserved questions leave the pending fields untouched. */
  private extractReservedIntake(qs: { id: string; q: string; options?: string[]; multi?: boolean }[]): void {
    for (const q of qs) {
      if (q.id === '__length') {
        const picks = this.intakeSel[this.intakeKey(q)] || [];
        for (const p of picks) {
          const m = p.match(/\d+/);
          if (m) { this.pendingSlideCount = parseInt(m[0], 10); break; }
        }
      } else if (q.id === '__framing') {
        const picks = this.intakeSel[this.intakeKey(q)] || [];
        for (const p of picks) {
          const lc = p.toLowerCase();
          if (lc.startsWith('overview')) { this.pendingFraming = 'overview'; break; }
          if (lc.startsWith('position')) { this.pendingFraming = 'positioning'; break; }
        }
      }
    }
  }
  private submitIntake(qs: { id: string; q: string; options?: string[]; multi?: boolean }[]): void {
    const lines = this.composeIntake(qs);
    if (!lines.length || this.loading) return;
    // Pull the chosen length / framing out of the reserved-id answers BEFORE
    // sending; the free-text "Here are my answers:" line still carries everything.
    this.extractReservedIntake(qs);
    this.intakeDone = { ...this.intakeDone, [this.intakeWidgetKey(qs)]: true };
    void this.send('Here are my answers:\n' + lines.join('\n'));
  }
  private skipIntake(qs: { id: string; q: string; options?: string[]; multi?: boolean }[]): void {
    if (this.loading) return;
    this.intakeDone = { ...this.intakeDone, [this.intakeWidgetKey(qs)]: true };
    void this.send('Use your best judgement for anything I haven’t specified — go ahead and draft it.');
  }

  // ── widget rendering ──────────────────────────────────────────────────
  private renderWidget(w: ChatWidget): TemplateResult | typeof nothing {
    switch (w.type) {
      case 'questions': {
        const qs = w.questions || [];
        const wkey = this.intakeWidgetKey(qs);
        const done = this.intakeDone[wkey];
        const answered = qs.some((q) => (this.intakeSel[this.intakeKey(q)]?.length || this.intakeOther[this.intakeKey(q)]?.trim()));
        return html`
          <div class="ws-widget ws-questions ${done ? 'is-done' : ''}">
            ${w.title ? html`<div class="ws-widget-title">${w.title}</div>` : nothing}
            ${qs.map((q) => {
              const qkey = this.intakeKey(q);
              return html`
              <div class="ws-q">
                <div class="ws-q-text">${q.q}</div>
                ${q.options?.length
                  ? html`<div class="qchips">${q.options.map((o) => html`
                      <button class="qchip ${(this.intakeSel[qkey] || []).includes(o) ? 'is-sel' : ''}"
                        ?disabled=${done} aria-pressed=${(this.intakeSel[qkey] || []).includes(o)}
                        @click=${() => this.toggleIntake(qkey, o)}>${o}</button>`)}
                    </div>`
                  : nothing}
                <input class="q-other" type="text" placeholder="Or type your own…"
                  ?disabled=${done} .value=${this.intakeOther[qkey] || ''}
                  @input=${(e: Event) => { this.intakeOther = { ...this.intakeOther, [qkey]: (e.target as HTMLInputElement).value }; }}
                  @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); this.submitIntake(qs); } }}>
              </div>`;
            })}
            ${done
              ? html`<div class="ws-q-sent faint">✓ Answers sent</div>`
              : html`<div class="ws-q-actions">
                  <button class="btn btn--ai btn--sm" ?disabled=${!answered || this.loading} @click=${() => this.submitIntake(qs)}>Send answers</button>
                  <button class="btn btn--ghost btn--sm" @click=${() => this.skipIntake(qs)}>Skip</button>
                </div>`}
          </div>`;
      }
      case 'outline':
        return html`
          <div class="ws-widget ws-outline">
            ${w.title ? html`<div class="ws-widget-title">${w.title}</div>` : nothing}
            <ol class="ws-outline-list">
              ${(w.items || []).map((it) => html`
                <li><span class="ws-kind">${String(it.kind ?? '')}</span> ${String(it.title ?? '')}</li>`)}
            </ol>
          </div>`;
      case 'sources':
        return html`
          <div class="ws-widget ws-sources">
            ${(w.items || []).map((it) => html`
              <div class="cite">${String(it.title ?? '')}${it.snippet ? html` — <span class="faint">${String(it.snippet)}</span>` : nothing}</div>`)}
          </div>`;
      case 'suggestions':
        return html`
          <div class="suggest-row">
            ${(w.items || []).map((it) => html`
              <button class="suggest" @click=${() => this.send(String(it.label ?? ''))}>${String(it.label ?? '')}</button>`)}
          </div>`;
      case 'callout':
        return html`<div class="ws-callout ws-callout--${w.tone || 'info'}">${w.text}</div>`;
      default:
        return nothing;
    }
  }

  /** A STABLE id for a message's feedback: the message's own id when present,
   *  otherwise its turn index within the thread (paired with the deckId on the
   *  wire so it is unique per deck). The index is stable within a session because
   *  the thread only ever grows; the deckId disambiguates across decks. */
  private msgRef(m: ThreadMsg, index: number): string {
    return m.id ? `id:${m.id}` : `turn:${index}`;
  }

  /** Record a thumbs-up / thumbs-down on one assistant message. Optimistic: the
   *  chosen thumb highlights immediately and the rating persists in thread state;
   *  the POST is fire-and-forget so a failure never blocks or breaks the chat. A
   *  thumbs-down reveals the optional "what was wrong?" one-liner. */
  private rate(m: ThreadMsg, index: number, rating: MessageFeedbackRating): void {
    if (!this.deckId) return; // nothing to attach feedback to yet
    const ref = this.msgRef(m, index);
    // Toggle off if the same thumb is clicked again (a mistap is undoable). We
    // still POST the new state so the server reflects the latest choice; an
    // un-rate clears the local highlight but leaves the last stored rating as-is.
    const next = this.ratings[ref] === rating ? undefined : rating;
    this.ratings = { ...this.ratings, [ref]: next as MessageFeedbackRating };
    if (next === undefined) { this.feedbackNoteFor = null; return; }
    this.feedbackNoteFor = next === 'down' ? ref : null;
    void this.postRating(ref, m.content, next);
  }

  /** Fire-and-forget POST of a rating (+ optional note). Soft-fails. */
  private async postRating(ref: string, message: string, rating: MessageFeedbackRating, note?: string): Promise<void> {
    if (!this.deckId) return;
    try {
      await api.rateMessage(this.deckId, { messageRef: ref, rating, message, note: note?.trim() || undefined });
    } catch { /* soft-fail: a failed rating must never break the chat */ }
  }

  /** Send the thumbs-down note (Enter in the one-liner). Re-posts the down rating
   *  WITH the note, then closes the input. Empty note just closes it (skippable). */
  private sendFeedbackNote(m: ThreadMsg, index: number, value: string): void {
    const ref = this.msgRef(m, index);
    const note = value.trim();
    if (note) void this.postRating(ref, m.content, 'down', note);
    this.feedbackNoteFor = null;
  }

  private renderMsg(m: ThreadMsg, index: number): TemplateResult {
    return html`
      <div class="msg ${m.role === 'user' ? 'msg--user' : ''}">
        <div class="msg-av">${m.role === 'user' ? 'You' : 'AI'}</div>
        <div class="msg-body">
          ${m.content ? html`<div class="msg-text">${unsafeHTML(mdToHtml(m.content))}</div>` : nothing}
          ${(m.widgets || []).map((w) => this.renderWidget(w))}
          ${m.role === 'assistant' && m.content ? this.renderFeedback(m, index) : nothing}
        </div>
      </div>`;
  }

  /** The subtle 👍 / 👎 affordance under an assistant message. Appears on hover
   *  (always-on once rated) so it stays out of the way. A thumbs-down reveals a
   *  tiny optional "what was wrong?" one-liner (Enter to send, skippable). */
  private renderFeedback(m: ThreadMsg, index: number): TemplateResult {
    const ref = this.msgRef(m, index);
    const rating = this.ratings[ref];
    const showNote = this.feedbackNoteFor === ref;
    return html`
      <div class="msg-fb ${rating ? 'is-rated' : ''}">
        <button class="msg-fb-btn ${rating === 'up' ? 'is-on is-up' : ''}"
          title="Good response" aria-label="Good response" aria-pressed=${rating === 'up'}
          @click=${() => this.rate(m, index, 'up')}>👍</button>
        <button class="msg-fb-btn ${rating === 'down' ? 'is-on is-down' : ''}"
          title="Bad response" aria-label="Bad response" aria-pressed=${rating === 'down'}
          @click=${() => this.rate(m, index, 'down')}>👎</button>
        ${showNote ? html`
          <input class="msg-fb-note" type="text" maxlength="500" placeholder="What was wrong? (optional — Enter to send)"
            aria-label="What was wrong with this response?"
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === 'Enter') { e.preventDefault(); this.sendFeedbackNote(m, index, (e.target as HTMLInputElement).value); }
              else if (e.key === 'Escape') { e.preventDefault(); this.feedbackNoteFor = null; }
            }}>` : nothing}
      </div>`;
  }

  // ── inline focused-slide editing ───────────────────────────────────────
  // The focused slide is rendered into the app's light DOM (not the iframe) so
  // its [data-region] elements can be edited directly. updated() repaints the
  // editor only when the focused slide changes, then wires contenteditable.
  protected updated(): void {
    // Track the deck title in the browser tab. Guarded on a cached value so we
    // only touch document.title when the title genuinely changes (this fires on
    // every studio re-render). No deck yet → plain "Folio".
    const docTitle = this.deck ? (this.deck.meta.title?.trim() || 'Untitled deck') : '';
    if (docTitle !== this.lastDocTitle) {
      this.lastDocTitle = docTitle;
      setDeckTitle(this.deck ? docTitle : undefined, !!this.deck);
    }
    // Keep the chat pinned to the latest message as the thread grows. Defer to
    // the next frame so it scrolls after the new messages have laid out.
    if (this.thread.length !== this.lastThreadLen) {
      this.lastThreadLen = this.thread.length;
      const t = this.querySelector('.chat-thread');
      // Bring the newest message into view. scrollIntoView resolves the target's
      // position even while sibling entrance animations settle (more reliable
      // than reading scrollHeight); behavior:'auto' ignores the CSS smooth-scroll.
      if (t) requestAnimationFrame(() => {
        const last = t.lastElementChild;
        if (last) last.scrollIntoView({ block: 'end', behavior: 'auto' });
        else t.scrollTop = t.scrollHeight;
      });
    }
    // Live thumbnails for the rail + grid + library + layout picker.
    if (this.deck) {
      mountThumbs(this, (id) => {
        if (id.startsWith('lib:')) {
          const tid = id.slice(4);
          for (const g of SLIDE_LIBRARY) { const t = g.items.find((x) => x.id === tid); if (t) return t.slide; }
          return undefined;
        }
        if (id.startsWith('lay:')) {
          // lay:<slideId>:<layoutId> — the focused slide previewed in a layout.
          const [, sid, lid] = id.split(':');
          const base = this.deck!.slides.find((s) => s.id === sid);
          if (!base) return undefined;
          return { ...base, layout: lid === '_' ? undefined : lid } as Slide;
        }
        if (id.startsWith('skin:')) {
          // skin:<slideId>:<variant|_> — the focused slide previewed in a skin.
          const [, sid, variant] = id.split(':');
          const base = this.deck!.slides.find((s) => s.id === sid);
          if (!base) return undefined;
          return { ...base, variant: variant === '_' ? '' : variant } as Slide;
        }
        const s = this.deck!.slides.find((x) => x.id === id);
        // Don't render a thumbnail for a not-yet-generated stub — it would have
        // no kind fields; the shimmering empty frame stands in until it lands.
        return s && this.pending.has(s.id) ? undefined : s;
      },
      (id) => (id.includes(':') ? undefined : this.overrides[id]),
      (id) => (id.includes(':') ? undefined : this.regionStyles[id]));
    }
    // Populate the rich-notes editor once per slide (never mid-edit — the
    // contenteditable owns its DOM after that, so re-renders don't reset it).
    if (this.notesOpen && this.focusSlide) {
      const rich = this.querySelector<HTMLElement>('.ne-rich');
      const key = this.focusSlide.id;
      if (rich && this.notesKey !== key) {
        rich.innerHTML = this.deck?.notes?.[key] || '';
        this.notesKey = key;
      }
    } else {
      this.notesKey = '';
    }
    if (this.focusIndex == null || !this.deck) { this.focusKey = ''; return; }
    const slide = this.deck.slides[this.focusIndex];
    if (!slide) return;
    const isPending = this.pending.has(slide.id);
    const key = `${slide.id}#${this.focusIndex}#${slide.variant || ''}#${(slide as { layout?: string }).layout || ''}#${(slide as { show?: { title?: boolean } }).show?.title === false ? 'nt' : ''}#${isPending ? 'p' : ''}`;
    if (key === this.focusKey) return; // already painted; don't clobber an active edit
    const host = this.querySelector<HTMLElement>('.focus-host');
    if (!host) return;
    // The host is about to be rebuilt — any in-progress inline edit (and its
    // contenteditable node) is destroyed by the innerHTML swap below, so tear
    // down the rich-text toolbar + its stray selectionchange listener first
    // (innerHTML replacement does NOT fire the node's blur handler).
    this.teardownRichEdit();
    this.focusKey = key;
    const stage = (window as unknown as { SlideStage?: {
      fitAll(r: Element): void; fit(scaler: Element): void;
      hydrate(s: Element, o: { animate: boolean }): void;
      destroyVideos?(scope: Element): void;
    } }).SlideStage;
    // Tear down any YouTube player on the outgoing slide before the innerHTML
    // swap orphans it (the swap does not fire the player's own cleanup).
    stage?.destroyVideos?.(host);
    if (isPending) {
      // A stub from the outline — no kind fields yet; show a placeholder while
      // its content streams in (rendering the stub would break data kinds).
      host.innerHTML = `<div class="focus-skeleton"><div class="canvas-spinner"></div><p>Generating “${slide.title || slide.kind}”…</p></div>`;
      return;
    }
    host.innerHTML =
      `<div class="slide-scaler fit-contain"><div class="slide-box"><div class="slide-mount">` +
      renderSlide(slide, this.overrides[slide.id] || {}, this.deck.meta, this.regionStyles[slide.id] || {}) +
      `</div></div></div>`;
    stage?.fitAll(host);
    host.querySelectorAll('.slide').forEach((s) => stage?.hydrate(s, { animate: false }));
    // Re-fit after layout settles — the first fit can run before the host has
    // its real height (place-items grid), which would leave the slide oversized.
    const scaler = host.querySelector('.slide-scaler');
    if (scaler) requestAnimationFrame(() => requestAnimationFrame(() => stage?.fit(scaler)));
    this.wireRegions(host);
  }

  private wireRegions(host: HTMLElement): void {
    host.querySelectorAll<HTMLElement>('[data-region]').forEach((el) => {
      el.classList.add('region-editable');
      el.setAttribute('tabindex', '0');
      el.setAttribute('role', 'button');
      el.setAttribute('aria-label', `Edit ${(el.getAttribute('data-region') || 'text').replace(/-/g, ' ')}`);
      const open = (e: Event) => {
        if (el.isContentEditable) return;
        e.stopPropagation();
        const region = el.getAttribute('data-region') || '';
        const r = el.getBoundingClientRect();
        this.selRect = { left: r.left, top: r.top, bottom: r.bottom };
        this.diff = null;
        this.sel = { region, mode: 'menu' };
      };
      el.addEventListener('click', open);
      // Double-click edits the text directly (same as the popover's Edit button),
      // skipping the menu — the quick path most editors expect.
      el.addEventListener('dblclick', (e: Event) => {
        e.preventDefault(); e.stopPropagation();
        this.sel = null;
        this.beginInlineEdit(el.getAttribute('data-region') || '');
      });
      el.addEventListener('keydown', (e: KeyboardEvent) => {
        if (!el.isContentEditable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); open(e); }
      });
    });
    // Image / gradient slots: click to change (generate · gradient · upload ·
    // remove). Mirrors the text data-region contract above.
    host.querySelectorAll<HTMLElement>('[data-img-region]').forEach((el) => {
      el.classList.add('img-editable');
      el.setAttribute('tabindex', '0');
      el.setAttribute('role', 'button');
      el.setAttribute('aria-label', 'Change image');
      const open = (e: Event) => {
        e.stopPropagation();
        const region = el.getAttribute('data-img-region') || '';
        const r = el.getBoundingClientRect();
        this.selRect = { left: r.left, top: r.top, bottom: r.bottom };
        this.sel = null; this.diff = null; this.imgGenSubject = '';
        this.imgTab = 'gen';
        // Default the popover's per-open style from the deck-level preference so
        // a single-slot generate matches a style chosen pre-gen / in generate-all.
        this.imgStyle = this.deckImgStyle;
        this.imgStyleText = this.deckImgStyleText;
        this.imgSel = { region };
      };
      el.addEventListener('click', open);
      el.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(e); }
      });
    });
  }

  render(): TemplateResult {
    return html`
      <div class="workspace" style="--chat-w:${this.chatW}px">
        <!-- LEFT: chat -->
        <section class="chat">
          <div class="chat-head">
            <span class="ws-title">AI Studio</span>
            <div class="chat-head-r">
              <span class="ws-status">${this.deck ? `${this.deck.slides.length} slides` : 'no deck yet'}</span>
              ${!this.loading && this.thread.length > 1 && this.thread[this.thread.length - 1]?.role === 'assistant'
                ? html`<button class="chat-new" title="Re-answer the last message" aria-label="Regenerate" @click=${() => this.regenerate()}>↻ Regenerate</button>`
                : nothing}
              ${this.thread.length > 1
                ? html`<button class="chat-new" title="Start a fresh chat (keeps the deck)" aria-label="New chat" @click=${() => this.newChat()}>✎ New chat</button>`
                : nothing}
            </div>
          </div>
          <div class="chat-thread" role="log" aria-live="polite" aria-label="Co-pilot conversation">
            ${this.thread.length === 0
              ? html`<div class="ws-empty-chat">
                  <p>Describe the deck you want. I'll ask a few questions, then build it.</p>
                  <div class="suggest-row">
                    <button class="suggest" @click=${() => this.send('Make a board review for Workplace by Ricoh')}>Board review for Workplace by Ricoh</button>
                    <button class="suggest" @click=${() => this.send('A short investor update on our platform traction')}>Investor update</button>
                  </div>
                </div>`
              : this.thread.map((m, i) => this.renderMsg(m, i))}
            ${this.loading ? html`<div class="msg"><div class="msg-av">AI</div><div class="msg-body"><div class="typing"><span class="dot"></span><span class="dot"></span><span class="dot"></span></div></div></div>` : nothing}
          </div>
          ${this.error ? html`<div class="ws-error">${this.error}</div>` : nothing}
          ${this.sources.length || this.uploading
            ? html`<div class="ws-sources-bar">
                ${this.uploading ? html`<span class="ws-src-chip is-uploading"><span class="chip-spin"></span>${this.uploading}</span>` : nothing}
                ${this.sources.map((s) => html`<span class="ws-src-chip">${s.filename}<button class="ws-src-x" aria-label=${`Remove ${s.filename}`} @click=${() => this.removeSource(s.id)}>×</button></span>`)}
              </div>`
            : nothing}
          <div class="chat-composer">
            <label class="ws-upload ${this.uploading ? 'is-busy' : ''}" title=${this.uploading ? 'Uploading…' : 'Attach a document to ground generation'} aria-label="Attach a document">
              <input type="file" hidden ?disabled=${!!this.uploading} @change=${(e: Event) => this.onUpload(e)} accept=".txt,.md,.csv,.json,.html,text/*,application/pdf">
              ${this.uploading ? html`<span class="up-spin"></span>` : '＋'}
            </label>
            <textarea class="input" rows="1" placeholder="Message the co-pilot…"
              .value=${this.input}
              @input=${(e: Event) => { this.input = (e.target as HTMLTextAreaElement).value; }}
              @keydown=${(e: KeyboardEvent) => this.onKey(e)}></textarea>
            <button class="send-btn ${this.loading ? 'is-stop' : ''}" aria-label=${this.loading ? 'Stop' : 'Send message'} title=${this.loading ? 'Stop' : 'Send'}
              ?disabled=${!this.loading && !this.input.trim()}
              @click=${() => (this.loading ? this.stop() : this.send(this.input))}>
              ${this.loading ? STOP_ICON : SEND_ICON}
            </button>
          </div>
        </section>

        <!-- draggable divider -->
        <div class="ws-resizer" title="Drag to resize the chat" @mousedown=${(e: MouseEvent) => this.startResize(e)}></div>

        <!-- RIGHT: canvas -->
        <section class="canvas">
          <div class="canvas-toolbar">
            <span class="ws-title">${this.deck?.meta.title || 'Canvas'}</span>
            <div class="ws-toolbar-actions">
              ${this.deck && this.deck.slides.length ? html`
                <button class="tbtn" title=${this.focusIndex == null ? 'Edit the selected slide' : 'See all slides as a grid'} @click=${() => { this.focusIndex = this.focusIndex == null ? 0 : null; }}>
                  ${this.focusIndex == null ? '▭ Edit slide' : '▦ All slides'}</button>
                <div class="theme-wrap" @click=${(e: Event) => e.stopPropagation()}>
                  <button class="tbtn" title="Theme & fonts for the whole deck" aria-haspopup="menu" aria-expanded=${this.themeOpen} @click=${() => { this.themeOpen = !this.themeOpen; this.brandOpen = false; }}>Theme ▾</button>
                  ${this.themeOpen ? this.renderThemeMenu() : nothing}
                </div>
                <div class="theme-wrap" @click=${(e: Event) => e.stopPropagation()}>
                  <button class="tbtn" title="Brand kit — customer logo, accent & tint" aria-haspopup="menu" aria-expanded=${this.brandOpen} @click=${() => { this.brandOpen = !this.brandOpen; this.themeOpen = false; }}>Brand ▾</button>
                  ${this.brandOpen ? this.renderBrandMenu() : nothing}
                </div>
                <button class="tbtn" ?disabled=${this.genAllBusy} title="Fill every empty image slot across the deck with AI imagery" aria-label="Generate images for the whole deck" @click=${() => this.generateAllImages()}>${this.genAllBusy ? `Generating… ${this.genAllProgress.done}/${this.genAllProgress.total}` : '✨ Generate images'}</button>
                <button class="tbtn" title="Present full screen" @click=${() => this.present()}>Present</button>
                <button class="tbtn" ?disabled=${this.sharing} @click=${() => this.share()}>${this.sharing ? 'Sharing…' : 'Share'}</button>
                <button class="tbtn tbtn--primary ${this.saveState === 'saved' ? 'is-saved' : ''}" ?disabled=${this.saving} title="Save (autosaves as you edit)" @click=${() => this.persist(false)}>${this.saveState === 'saving' ? 'Saving…' : this.saveState === 'saved' ? '✓ Saved' : 'Save'}</button>
              ` : nothing}
            </div>
          </div>
          ${this.shareUrl
            ? html`<div class="share-bar"><span class="share-url">${this.shareUrl}</span>
                <button class="tbtn btn--sm" @click=${() => this.copyShare()}>Copy</button>
                <button class="icon-btn" aria-label="Dismiss" @click=${() => { this.shareUrl = ''; }}>×</button></div>`
            : nothing}
          ${this.genNotice
            ? html`<div class="share-bar gen-notice ${this.genNotice.error ? 'is-error' : ''}" role="status"><span class="gen-notice-msg">${this.genNotice.msg}</span>
                <button class="icon-btn" aria-label="Dismiss" @click=${() => { this.genNotice = null; }}>×</button></div>`
            : nothing}
          ${this.isBuilding()
            ? this.renderBuildingDeck()
            : this.deck && this.deck.slides.length
            ? html`
                ${this.focusIndex != null
                  ? html`
                      ${this.renderSlideTools()}
                      <div class="canvas-main slide-frame-wrap"
                        @click=${() => { this.sel = null; this.imgSel = null; this.cardEdit = false; this.toolMenu = null; this.rtColourOpen = false; }}
                        @contextmenu=${(e: MouseEvent) => this.onContext(e)}>
                        <div class="focus-host ${this.aiEditing || (this.focusSlide && this.aiBusy.has(this.focusSlide.id)) ? 'shimmer' : ''}" aria-busy=${this.aiEditing} data-theme=${this.slideTheme(this.focusSlide)} data-font=${this.slideFont(this.focusSlide)}></div>
                      </div>`
                  : html`
                      <div class="grid-view" role="list" aria-label="All slides">
                        <div class="grid-grid">
                          ${this.deck.slides.map((s, i) => html`
                            <button class="grid-cell ${this.dragFrom === i ? 'dragging' : ''} ${this.aiBusy.has(s.id) ? 'thumb-busy' : ''}"
                              role="listitem" aria-label=${`Slide ${i + 1}: ${s.label || s.kind} — click to edit`}
                              draggable="true"
                              @click=${() => { this.focusIndex = i; }}
                              @dragstart=${() => { this.dragFrom = i; }}
                              @dragover=${(e: DragEvent) => { e.preventDefault(); this.dragSwap(i); }}
                              @dragend=${() => this.endDrag()}
                              @contextmenu=${(e: MouseEvent) => { this.focusIndex = i; this.onContext(e); }}>
                              <div class="gc-frame" data-thumb=${s.id} data-theme=${this.slideTheme(s)} data-font=${this.slideFont(s)}></div>
                              <div class="gc-label">${s.label || s.kind}</div>
                            </button>`)}
                        </div>
                      </div>`}
                <div class="rail">
                  ${this.deck.slides.map((s, i) => html`
                    <button class="rail-thumb ${this.focusIndex === i ? 'is-active' : ''} ${this.dragFrom === i ? 'dragging' : ''} ${this.aiBusy.has(s.id) ? 'thumb-busy' : ''}"
                      draggable="true" aria-label=${`Slide ${i + 1}: ${s.kind}`} aria-current=${this.focusIndex === i}
                      @click=${() => { this.focusIndex = i; }}
                      @dragstart=${() => { this.dragFrom = i; }}
                      @dragover=${(e: DragEvent) => { e.preventDefault(); this.dragSwap(i); }}
                      @dragend=${() => this.endDrag()}>
                      <span class="rail-thumb-n">${i + 1}</span>
                      ${(s as { theme?: string }).theme && THEME_IDS.includes((s as { theme?: string }).theme!) ? html`<span class="rail-thumb-theme" title=${`This slide has its own theme (${(s as { theme?: string }).theme}). Reset via Theme ▸ This slide only ▸ Follow deck.`}></span>` : nothing}
                      <div class="rail-thumb-frame" data-thumb=${s.id} data-theme=${this.slideTheme(s)} data-font=${this.slideFont(s)}></div>
                    </button>`)}
                  <button class="rail-add" title="Insert a slide" aria-label="Insert a slide" @click=${() => { this.libMode = 'insert'; this.libOpen = true; }}>＋</button>
                </div>
                ${this.rtBar ? this.renderRichToolbar() : nothing}
                ${this.sel ? this.renderEditPop() : nothing}
                ${this.imgSel ? this.renderImgPop() : nothing}
                ${this.cardEdit ? this.renderCardEditPop() : nothing}
                ${this.diff ? this.renderDiffPop() : nothing}
                ${this.libOpen ? this.renderLibrary() : nothing}
                ${this.notesOpen && this.focusSlide ? this.renderNotes() : nothing}
                ${this.confirm ? this.renderConfirm() : nothing}
                ${this.genAllOpen ? this.renderGenAllDialog() : nothing}`
            : this.loading
              ? html`<div class="canvas-empty canvas-pregen">
                  <div class="pregen-head"><div class="canvas-spinner" aria-label="Loading deck"></div><p>Opening your deck…</p></div>
                </div>`
              : html`<div class="canvas-empty canvas-pregen is-pristine">
                    <div class="pregen-head"><div class="ph-icon">▦</div><p>Pick a look to start — optional. Then describe your deck on the left.</p></div>
                    ${this.renderPreGenPanel()}
                    <p class="pregen-foot faint">Your deck will appear here once the co-pilot drafts it.</p>
                  </div>`}
        </section>
      </div>`;
  }

  // ── canvas sub-renders ────────────────────────────────────────────────
  private renderSlideTools(): TemplateResult {
    const curAccent = (getComputedStyle(document.documentElement).getPropertyValue('--brand-color').trim() || '#00A76F').toLowerCase();
    const canBg = !!this.focusSlide && (KIND_META[this.focusSlide.kind]?.backgrounds.length ?? 0) > 1;
    return html`
      <div class="slide-tools" role="toolbar" aria-label="Slide tools" @click=${(e: Event) => e.stopPropagation()}>
        <button class="tbtn tbtn--ai" title="Edit this card with AI — a free-text instruction or a quick action" aria-haspopup="dialog" aria-expanded=${this.cardEdit} @click=${(e: Event) => this.openCardEdit(e)}>✦ Edit this card</button>
        <span class="sep" role="separator"></span>
        <button class="tbtn" title="Layout — genuinely distinct arrangements for this slide" aria-haspopup="menu" aria-expanded=${this.toolMenu === 'layout'} @click=${() => { this.toolMenu = this.toolMenu === 'layout' ? null : 'layout'; }}>Layout ▾</button>
        <button class="tbtn" title="Skin — surface / ink treatment" aria-haspopup="menu" aria-expanded=${this.toolMenu === 'skin'} @click=${() => { this.toolMenu = this.toolMenu === 'skin' ? null : 'skin'; }}>Skin ▾</button>
        ${canBg ? html`<button class="tbtn" title="Slide background — gradient gallery, image, or a YouTube video" aria-haspopup="menu" aria-expanded=${this.toolMenu === 'bg'} @click=${() => { this.bgVideoUrl = ''; this.bgVideoErr = ''; this.toolMenu = this.toolMenu === 'bg' ? null : 'bg'; }}>Background ▾</button>` : nothing}
        <span class="tool-anchor">
          <button class="tbtn" title="Accent colour" aria-haspopup="menu" aria-expanded=${this.toolMenu === 'accent'} @click=${() => { this.toolMenu = this.toolMenu === 'accent' ? null : 'accent'; }}><span class="swatch"></span>Accent ▾</button>
          ${this.toolMenu === 'accent'
            ? html`<div class="menu acc-menu" role="menu">
                <div class="accent-swatches">
                  ${ACCENTS.map((a) => html`<button class="accent-sw ${curAccent === a.hex.toLowerCase() ? 'on' : ''}" style="background:${a.hex}" title=${a.name} aria-label=${a.name} @click=${() => this.setAccent(a.hex)}></button>`)}
                </div>
                <input type="color" class="acc-custom" aria-label="Custom accent colour" .value=${/^#[0-9a-fA-F]{6}$/.test(curAccent) ? curAccent : '#00a76f'} @input=${(e: Event) => this.setAccent((e.target as HTMLInputElement).value)}>
              </div>`
            : nothing}
        </span>
        ${this.titleToggleable() ? html`<button class="tbtn ${this.titleShown() ? 'is-on' : ''}" title="Show or hide the slide title" aria-pressed=${this.titleShown()} @click=${() => this.toggleTitle()}>Title</button>` : nothing}
        <button class="tbtn" title="Change the slide type — pick a layout with a preview" @click=${() => { this.libMode = 'replace'; this.libOpen = true; }}>Change type…</button>
        <span class="sep" role="separator"></span>
        <button class="tbtn" title="Duplicate this slide" aria-label="Duplicate slide" @click=${() => this.dupSlide(this.focusIndex!)}>⧉ Duplicate</button>
        <button class="tbtn" title="Insert a new slide from the gallery" aria-label="Insert slide" @click=${() => { this.libMode = 'insert'; this.libOpen = true; }}>＋ Insert</button>
        <button class="tbtn tbtn--danger" title="Delete this slide (asks to confirm)" aria-label="Delete slide" @click=${() => this.delSlide(this.focusIndex!)}>🗑 Delete</button>
        <span class="sep" role="separator"></span>
        <button class="tbtn ${this.notesOpen ? 'is-on' : ''}" title="Speaker notes for this slide" aria-pressed=${this.notesOpen} @click=${() => { this.notesOpen = !this.notesOpen; }}>Notes${(this.stripHtml(this.deck?.notes?.[this.focusSlide?.id || ''] || '')).trim() ? html`<span class="tbtn-dot"></span>` : nothing}</button>
        ${this.toolMenu === 'layout' ? this.renderLayoutPicker() : nothing}
        ${this.toolMenu === 'skin' ? this.renderSkinPicker() : nothing}
        ${this.toolMenu === 'bg' && canBg ? this.renderBgPicker() : nothing}
      </div>`;
  }

  /** Layout picker — the kind's named, genuinely-distinct layouts (from
   *  KIND_META), each previewed live in that layout. */
  private renderLayoutPicker(): TemplateResult {
    const slide = this.focusSlide;
    if (!slide) return html``;
    const cur = this.curLayout(slide);
    const layouts = KIND_META[slide.kind].layouts;
    return html`
      <div class="layout-pop" role="menu" aria-label="Choose a layout">
        <div class="layout-pop-grid">
          ${layouts.map((l) => html`
            <button class="layout-card ${cur === l.id ? 'on' : ''}" role="menuitemradio" aria-checked=${cur === l.id} title=${l.blurb || l.label} @click=${() => this.setLayout(l.id)}>
              <div class="lay-thumb" data-thumb=${`lay:${slide.id}:${l.id}`} data-theme=${this.slideTheme(slide)} data-font=${this.slideFont(slide)}></div>
              <div class="lay-name">${cur === l.id ? html`<span class="lay-tick">✓</span>` : nothing}${l.label}</div>
            </button>`)}
        </div>
      </div>`;
  }

  /** Skin picker — the surface/ink treatment, separate from geometry (layout). */
  private renderSkinPicker(): TemplateResult {
    const slide = this.focusSlide;
    if (!slide) return html``;
    const v = (slide as { variant?: string }).variant || '';
    const SKINS: { v: string; label: string }[] = [
      { v: '', label: 'Standard' }, { v: 's-surface', label: 'Surface card' }, { v: 's-ink', label: 'Ink band' },
    ];
    return html`
      <div class="layout-pop" role="menu" aria-label="Choose a skin">
        <div class="layout-pop-grid">
          ${SKINS.map((l) => html`
            <button class="layout-card ${v === l.v ? 'on' : ''}" role="menuitemradio" aria-checked=${v === l.v} @click=${() => this.setVariant(l.v)}>
              <div class="lay-thumb" data-thumb=${`skin:${slide.id}:${l.v || '_'}`} data-theme=${this.slideTheme(slide)} data-font=${this.slideFont(slide)}></div>
              <div class="lay-name">${v === l.v ? html`<span class="lay-tick">✓</span>` : nothing}${l.label}</div>
            </button>`)}
        </div>
      </div>`;
  }

  /** The slide's effective background, reading the structured field first and
   *  falling back to the legacy top-level art/img (mirrors the renderer). */
  private curBg(slide: Slide | null): SlideBackground {
    const s = slide as (Slide & { background?: SlideBackground; art?: string; img?: string }) | null;
    if (s?.background?.type) return s.background;
    if (s?.img) return { type: 'image', img: s.img };
    if (s?.art) return { type: 'gradient', art: s.art as SlideBackground['art'] };
    return { type: 'none' };
  }

  /** Background picker — a Type row (the kinds the catalogue allows) plus the
   *  gradient gallery, image upload, or colour-wash strength as appropriate. */
  private renderBgPicker(): TemplateResult {
    const slide = this.focusSlide;
    if (!slide) return html``;
    const types = (KIND_META[slide.kind]?.backgrounds ?? ['none']) as BgType[];
    const bg = this.curBg(slide);
    const showArt = bg.type === 'gradient' || bg.type === 'motion';
    const showImg = bg.type === 'image' || bg.type === 'image-split';
    const TINTS = [{ t: 8, l: 'Subtle' }, { t: 16, l: 'Medium' }, { t: 28, l: 'Strong' }];
    return html`
      <div class="bg-pop" role="menu" aria-label="Choose a background">
        <div class="bg-label">Type</div>
        <div class="bg-types">
          ${types.map((t) => html`
            <button class="bg-type ${bg.type === t ? 'on' : ''}" role="menuitemradio" aria-checked=${bg.type === t}
              @click=${() => this.setBgType(t)}>${BG_TYPE_LABELS[t] || t}</button>`)}
        </div>
        ${showArt ? html`
          <div class="bg-label">Gradient</div>
          <div class="bg-grid">
            ${ART_OPTIONS.map((a) => html`
              <button class="bg-sw bg-art-${a.id} ${bg.art === a.id ? 'on' : ''}" title=${a.name} aria-label=${a.name} @click=${() => this.setBgArt(a.id)}></button>`)}
          </div>` : nothing}
        ${showImg ? html`
          <div class="bg-grid">
            ${bg.img ? html`<button class="bg-sw on" title="Current image"><img src=${bg.img} alt=""></button>` : nothing}
          </div>
          <div class="bg-upload">
            <label class="bg-upload-btn">
              <input type="file" hidden accept="image/png,image/jpeg,image/webp,image/avif" @change=${(e: Event) => this.onBgUpload(e)}>
              ${this.uploading ? 'Uploading…' : (bg.img ? '⬆ Replace image' : '⬆ Upload an image')}
            </label>
          </div>` : nothing}
        ${bg.type === 'colour' ? html`
          <div class="bg-label">Strength</div>
          <div class="bg-types">
            ${TINTS.map((x) => html`
              <button class="bg-type ${(bg.tint ?? 14) === x.t ? 'on' : ''}" @click=${() => this.setBgTint(x.t)}>${x.l}</button>`)}
          </div>` : nothing}
        ${bg.type === 'video' ? html`
          <div class="bg-label">YouTube link</div>
          ${bg.videoId ? html`
            <div class="bg-grid">
              <button class="bg-sw on" title="Current video">
                <img src="https://img.youtube.com/vi/${bg.videoId}/default.jpg" alt="">
              </button>
            </div>` : nothing}
          <div class="bg-upload" style="flex-direction:column;align-items:stretch;gap:8px">
            <input class="imgp-subj" placeholder="Paste a YouTube URL…" .value=${this.bgVideoUrl}
              @input=${(e: Event) => { this.bgVideoUrl = (e.target as HTMLInputElement).value; this.bgVideoErr = ''; }}
              @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.applyBgVideo(); }}>
            <button class="btn btn--sm" @click=${() => this.applyBgVideo()}>
              ${bg.videoId ? 'Replace video' : 'Set video'}
            </button>
            ${this.bgVideoErr ? html`<span class="bg-video-err" style="color:var(--error-strong);font-size:0.8rem">${this.bgVideoErr}</span>` : nothing}
          </div>
          <p class="bg-hint" style="margin:8px 0 0;font-size:0.78rem;color:var(--slide-text-3,var(--text-muted,#888))">
            Plays in the editor and Present; shared/live viewers show the video thumbnail.
          </p>` : nothing}
      </div>`;
  }

  /** Parse the pasted YouTube URL to an 11-char id and set a video background.
   *  An unrecognised paste sets a friendly error and leaves the slide unchanged
   *  — the model only ever stores a valid id, never a raw URL. */
  private applyBgVideo(): void {
    if (this.focusIndex == null) return;
    const id = youtubeId(this.bgVideoUrl);
    if (!id) { this.bgVideoErr = 'That doesn’t look like a YouTube link.'; return; }
    this.bgVideoErr = '';
    this.mutateSlides((s) => {
      const cur = s[this.focusIndex!] as Slide & { background?: SlideBackground; art?: string; img?: string };
      delete cur.art; delete cur.img;
      cur.background = { type: 'video', videoId: id };
    });
    this.bgVideoUrl = '';
  }

  /** Switch the background TYPE, carrying art/img across where it still applies. */
  private setBgType(type: BgType): void {
    if (this.focusIndex == null) return;
    const prev = this.curBg(this.focusSlide);
    this.mutateSlides((s) => {
      const cur = s[this.focusIndex!] as Slide & { background?: SlideBackground; art?: string; img?: string };
      delete cur.art; delete cur.img; // legacy fields retire in favour of the structured background
      if (type === 'none') { cur.background = { type: 'none' }; return; }
      if (type === 'colour') { cur.background = { type: 'colour', tint: prev.tint ?? 14 }; return; }
      if (type === 'gradient' || type === 'motion') { cur.background = { type, art: prev.art || 'slate' }; return; }
      if (type === 'video') { cur.background = { type: 'video', ...(prev.videoId ? { videoId: prev.videoId } : {}) }; return; }
      // image / image-split: keep any current image; side defaults left for split
      cur.background = { type, img: prev.img, ...(type === 'image-split' ? { side: prev.side || 'left' } : {}) };
    });
    if (type === 'none') this.toolMenu = null;
  }

  /** Pick a named gradient (keeps the current gradient/motion type). */
  private setBgArt(art: string): void {
    if (this.focusIndex == null) return;
    this.mutateSlides((s) => {
      const cur = s[this.focusIndex!] as Slide & { background?: SlideBackground; art?: string; img?: string };
      const type: BgType = cur.background?.type === 'motion' ? 'motion' : 'gradient';
      delete cur.art; delete cur.img;
      cur.background = { type, art: art as SlideBackground['art'] };
    });
  }

  /** Set the colour-wash strength. */
  private setBgTint(tint: number): void {
    if (this.focusIndex == null) return;
    this.mutateSlides((s) => {
      const cur = s[this.focusIndex!] as Slide & { background?: SlideBackground };
      cur.background = { type: 'colour', tint };
    });
  }

  /** Upload a background image. Embedded as a data URL so the slide stays part of
   *  the self-contained, shareable artifact (no external bucket dependency the
   *  exported HTML could fail to reach). */
  private async onBgUpload(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || this.focusIndex == null) return;
    this.error = '';
    if (file.size > 6 * 1024 * 1024) {
      this.error = `That image is ${(file.size / 1048576).toFixed(1)} MB — please keep slide images under 6 MB.`;
      input.value = ''; return;
    }
    this.uploading = file.name;
    try {
      const dataUrl = await fileToDataUrl(file);
      // Persist to the deck's source bucket too (best-effort grounding/backup).
      if (this.deckId) {
        try {
          const b64 = dataUrl.split(',')[1] || '';
          await api.uploadSource(this.deckId, { filename: file.name, mime: file.type || 'image/png', contentBase64: b64 });
        } catch { /* the embedded data URL is the source of truth; bucket is a bonus */ }
      }
      this.mutateSlides((s) => {
        const cur = s[this.focusIndex!] as Slide & { background?: SlideBackground; art?: string; img?: string };
        const type: BgType = cur.background?.type === 'image-split' ? 'image-split' : 'image';
        delete cur.art; delete cur.img;
        cur.background = { type, img: dataUrl, ...(type === 'image-split' ? { side: cur.background?.side || 'left' } : {}) };
      });
    } catch {
      this.error = 'Couldn’t read that image.';
    } finally {
      this.uploading = '';
      input.value = '';
      this.toolMenu = null;
    }
  }

  // ── per-slot image editing ────────────────────────────────────────────────
  // A "slot" is any image/gradient surface keyed by data-img-region:
  //  • 'bg'      → the slide background (mirrors the Background ▾ menu)
  //  • 'panel'   → the side rail's panelImg/panelArt
  //  • 'gal:<i>' → gallery cell i's img/art
  /** Read the current image/gradient on a slot, for the popover's state. */
  private curSlot(region: string): { img?: string; art?: string } {
    const s = this.focusSlide as (Slide & { panelImg?: string; panelArt?: string; items?: { img?: string; art?: string }[] }) | null;
    if (!s) return {};
    if (region === 'panel') return { img: s.panelImg, art: s.panelArt };
    if (region.startsWith('gal:')) { const it = s.items?.[Number(region.slice(4))]; return { img: it?.img, art: it?.art }; }
    const bg = this.curBg(this.focusSlide);
    return { img: bg.img, art: bg.art };
  }

  /** Read the REAL image on a slot of ANY slide (not just the focused one) — the
   *  per-(slide,region) emptiness check the whole-deck generate-all loop uses.
   *  Only an actual image counts as "filled"; a chosen gradient (art) is empty,
   *  so generate-all still fills a gradient-only slot. Mirrors curSlot's region
   *  decoding + curBg's legacy/structured background resolution. */
  /** The RAW stored value of an image slot — may be a real src, a data: URL, an
   *  unresolved `generate:<subject>` sentinel, or ''. Use slotImageOf() for the
   *  "is this slot actually showing an image?" check. */
  private rawSlot(slide: Slide, region: string): string {
    const s = slide as Slide & { panelImg?: string; items?: { img?: string }[] };
    if (region === 'panel') return s.panelImg || '';
    if (region.startsWith('gal:')) return s.items?.[Number(region.slice(4))]?.img || '';
    return this.curBg(slide).img || '';
  }

  /** Whether a slot is FILLED with a DISPLAYABLE image — mirrors the renderer's
   *  resolvedSrc, so an unresolved `generate:` sentinel (or any src srcAttr would
   *  reject) reads as EMPTY and is therefore eligible for the auto-fill. Without
   *  this, a model that sets background.img="generate:…" on the cover left the
   *  slot looking "filled" to the fill logic while the renderer drew a
   *  placeholder — so no image was ever generated. */
  private slotImageOf(slide: Slide, region: string): string {
    return resolvedSrc(this.rawSlot(slide, region));
  }

  /** Set a real image (generated src or uploaded data URL) on a slot of the
   *  FOCUSED slide. Thin wrapper over setSlotImgAt(this.focusIndex, …). */
  private setSlotImg(region: string, img: string): void {
    if (this.focusIndex == null) return;
    this.setSlotImgAt(this.focusIndex, region, img);
  }

  /** Set a real image on a slot of ANY slide by index — the index-addressed
   *  variant the whole-deck generate-all loop uses (setSlotImg only ever targets
   *  the focused slide). All mutation flows through mutateSlides so the canvas +
   *  autosave update exactly as a focused edit does. */
  private setSlotImgAt(slideIndex: number, region: string, img: string): void {
    this.mutateSlides((s) => {
      const cur = s[slideIndex] as Slide & { background?: SlideBackground; art?: string; img?: string; panelImg?: string; panelArt?: string; items?: { img?: string; art?: string }[]; side?: string };
      if (!cur) return;
      if (region === 'panel') { cur.panelImg = img; delete cur.panelArt; return; }
      if (region.startsWith('gal:')) {
        const i = Number(region.slice(4));
        const items = (cur as { items?: { img?: string; art?: string }[] }).items;
        const it = items?.[i];
        if (it) items![i] = { ...it, img, art: undefined };
        return;
      }
      const type: BgType = cur.background?.type === 'image-split' ? 'image-split' : 'image';
      delete cur.art; delete cur.img;
      cur.background = { type, img, ...(type === 'image-split' ? { side: cur.background?.side || 'left' } : {}) };
    });
  }

  /** Pick a named gradient for a slot. */
  private setSlotArt(region: string, art: string): void {
    if (this.focusIndex == null) return;
    this.mutateSlides((s) => {
      const cur = s[this.focusIndex!] as Slide & { background?: SlideBackground; art?: string; img?: string; panelImg?: string; panelArt?: string; items?: { img?: string; art?: string }[] };
      if (region === 'panel') { cur.panelArt = art as SlideBackground['art']; delete cur.panelImg; return; }
      if (region.startsWith('gal:')) {
        const i = Number(region.slice(4));
        const items = (cur as { items?: { img?: string; art?: string }[] }).items;
        const it = items?.[i];
        if (it) items![i] = { ...it, art, img: undefined };
        return;
      }
      const type: BgType = cur.background?.type === 'motion' ? 'motion' : 'gradient';
      delete cur.art; delete cur.img;
      cur.background = { type, art: art as SlideBackground['art'] };
    });
  }

  /** Clear a slot back to empty (no image, no gradient). */
  private clearSlot(region: string): void {
    if (this.focusIndex == null) return;
    this.mutateSlides((s) => {
      const cur = s[this.focusIndex!] as Slide & { background?: SlideBackground; art?: string; img?: string; panelImg?: string; panelArt?: string; items?: { img?: string; art?: string }[] };
      if (region === 'panel') { delete cur.panelImg; delete cur.panelArt; return; }
      if (region.startsWith('gal:')) {
        const i = Number(region.slice(4));
        const items = (cur as { items?: { img?: string; art?: string }[] }).items;
        const it = items?.[i];
        if (it) items![i] = { ...it, img: undefined, art: undefined };
        return;
      }
      delete cur.art; delete cur.img;
      cur.background = { type: 'none' };
    });
    this.imgSel = null;
  }

  /** The image theme to feed generation/prompt-assist: the focused slide's
   *  effective deck/slide theme mapped to an image theme — or undefined when the
   *  "Use theme style" toggle is off (the generator then picks its own). */
  private mappedImgTheme(): 'light' | 'dark' | 'coral' | 'navy' | undefined {
    return this.imgUseTheme ? imageThemeFor(this.slideTheme(this.focusSlide)) : undefined;
  }

  /** A short, plain-text context string for the focused slide (title + lede +
   *  kind) — fed to the AI prompt-assist op so "Auto" can write a relevant
   *  subject. Never reaches HTML; it only shapes the Imagen prompt. */
  private slideContext(): string {
    const s = this.focusSlide as (Slide & { title?: string; lede?: string }) | null;
    if (!s) return '';
    const bits = [this.stripHtml(s.title || ''), this.stripHtml(s.lede || ''), `(${s.kind} slide)`];
    return bits.filter((b) => b.trim()).join(' — ').slice(0, 400);
  }

  /** A short, plain-text context string for the whole deck (title + subtitle). */
  private deckContext(): string {
    const m = this.deck?.meta;
    if (!m) return '';
    return [m.title, m.subtitle].filter((x) => (x || '').trim()).join(' — ').slice(0, 240);
  }

  /** "Auto" — ask the AI to write a subject from the slide + deck + theme
   *  context. The result is staged into the describe input for review; the user
   *  hits Generate themselves (we never auto-generate). */
  private async autoImgPrompt(): Promise<void> {
    if (this.imgBusy) return;
    this.imgBusy = true; this.error = '';
    try {
      const prompt = await api.imagePrompt({
        mode: 'auto',
        slide: this.slideContext() || undefined,
        deck: this.deckContext() || undefined,
        theme: this.mappedImgTheme(),
      });
      this.imgGenSubject = prompt;
    } catch (e) {
      this.error = e instanceof HttpError ? `Couldn’t suggest a prompt (${e.status}).` : 'Couldn’t suggest a prompt.';
    } finally {
      this.imgBusy = false;
    }
  }

  /** "Enhance prompt" — improve the current draft subject in place. */
  private async enhanceImgPrompt(): Promise<void> {
    const draft = this.imgGenSubject.trim();
    if (!draft || this.imgBusy) return;
    this.imgBusy = true; this.error = '';
    try {
      const prompt = await api.imagePrompt({
        mode: 'enhance',
        draft,
        slide: this.slideContext() || undefined,
        deck: this.deckContext() || undefined,
        theme: this.mappedImgTheme(),
      });
      this.imgGenSubject = prompt;
    } catch (e) {
      this.error = e instanceof HttpError ? `Couldn’t enhance that prompt (${e.status}).` : 'Couldn’t enhance that prompt.';
    } finally {
      this.imgBusy = false;
    }
  }

  /** Generate a real image for a slot via Vertex Imagen — using the chosen art
   *  style (or a custom description) and, when the theme toggle is on, the
   *  deck-mapped image theme. */
  private async genSlotImg(region: string): Promise<void> {
    const subject = this.imgGenSubject.trim();
    if (!subject || this.imgBusy) return;
    this.imgBusy = true; this.error = '';
    try {
      const aspect = this.aspectFor(region);
      const custom = this.imgStyle === 'custom';
      const styleText = custom ? this.imgStyleText.trim() : '';
      // A style chosen for a single slot becomes the deck's preference too, so the
      // next slot / generate-all / a re-open of the popover stays consistent.
      this.deckImgStyle = this.imgStyle;
      this.deckImgStyleText = this.imgStyleText;
      const res = await api.generateImage({
        subject,
        style: custom ? 'editorial-photo' : this.imgStyle,
        ...(custom && styleText ? { styleText } : {}),
        ...(this.mappedImgTheme() ? { theme: this.mappedImgTheme() } : {}),
        aspect,
      });
      this.setSlotImg(region, res.src);
      this.imgGenSubject = '';
      this.imgSel = null;
    } catch (e) {
      this.error = e instanceof HttpError ? `Couldn’t generate that image (${e.status}).` : 'Couldn’t generate that image.';
    } finally {
      this.imgBusy = false;
    }
  }

  // ── whole-deck image fill ──────────────────────────────────────────────────
  /** Aspect ratio for a slot region — bg is 16:9, gallery cells 1:1, panel 4:3.
   *  Shared by the single-slot generate and the whole-deck pass. */
  private aspectFor(region: string): '16:9' | '4:3' | '1:1' {
    return region === 'bg' ? '16:9' : region.startsWith('gal:') ? '1:1' : '4:3';
  }

  /** A compact plain-text context (title + lede + kind) for ANY slide — feeds the
   *  "auto" prompt-assist op so generate-all writes a per-slide subject. Mirrors
   *  slideContext() but takes the slide rather than reading the focused one. */
  private slideTextContext(slide: Slide): string {
    const s = slide as Slide & { title?: string; lede?: string };
    const bits = [this.stripHtml(s.title || ''), this.stripHtml(s.lede || ''), `(${s.kind} slide)`];
    return bits.filter((b) => b.trim()).join(' — ').slice(0, 400);
  }

  /** Max images one whole-deck pass will generate (cost/latency cap). Empties
   *  beyond this are left for a follow-up run and reported in the summary. */
  private static readonly GEN_ALL_CAP = 12;

  // ── progressive per-slide image fill (Task 3) ──────────────────────────────
  /** Hard per-deck ceiling on the progressive (per-slide) auto fill — once this
   *  many slots have been STARTED in one generation run, no more are enqueued, so
   *  a large fresh deck never spends unbounded Vertex calls. */
  private static readonly AUTO_FILL_CEILING = 30;
  /** Consecutive "Vertex unconfigured" failures (503 / ErrNotConfigured) after
   *  which the progressive fill short-circuits for the rest of the run, so a dead
   *  image backend isn't hammered slot after slot. */
  private static readonly AUTO_FILL_503_LIMIT = 2;
  /** Bounded concurrency for BOTH the progressive per-slide fill and the manual
   *  generate-all loop. */
  private static readonly FILL_CONCURRENCY = 3;

  // ── structural check→correct pass (Task 5) ─────────────────────────────────
  /** Max overpacked slides the post-generation self-check will correct in one
   *  run — a hard cost budget so a badly-overpacked deck can't fan out into an
   *  unbounded number of model calls. Surplus overpacked slides are left as-is
   *  (the auto-fit floor still keeps them readable, just smaller). */
  private static readonly CORRECT_BUDGET = 6;
  /** Max tighten rounds spent on a SINGLE overpacked slide before giving up and
   *  keeping the best version seen. Mirrors create-page.ts autoFitRevise's
   *  maxRounds=2 discipline. */
  private static readonly CORRECT_ROUNDS = 2;
  /** Bounded concurrency for the overpacked-slide corrections (each is a model
   *  call) — kept at/below the image-fill concurrency. */
  private static readonly CORRECT_CONCURRENCY = 2;
  /** How long the self-check waits for the progressive image fill to drain before
   *  measuring, so a correction never races an in-flight per-slide fill. */
  private static readonly AUTO_FILL_IDLE_TIMEOUT_MS = 30000;

  // ── advisory "quick visual" check→correct pass (Task 6) ────────────────────
  /** Feature flag for the advisory visual-review correction pass. When true, a
   *  fresh generation runs ONE batched LLM visual review after the structural
   *  self-check and applies a bounded number of legibility/visual corrections.
   *  A static constant (not a server flag) so it can be flipped in one place;
   *  default ON. The pass is soft-failing, so leaving it on is safe even when AI
   *  is down (the server returns no issues → no corrections). */
  private static readonly VISUAL_CHECK_ENABLED = true;
  /** Max single-slide corrections the advisory visual pass will apply in one run
   *  — a hard cost bound so a review that flags many slides can't fan out into an
   *  unbounded number of edit calls. Surplus issues are left unactioned. */
  private static readonly VISUAL_CORRECT_BUDGET = 3;

  /** A `slide` stream event landed: enqueue that slide's EMPTY image-capable slots
   *  for background generation, then nudge the bounded worker pool. Runs only on a
   *  FRESH first generation (autoFillThisRun) — a refine turn, or a manual
   *  generate-all running (genAllBusy), or a short-circuited/aborted run all skip.
   *  Empty-only is the natural guard against re-imaging an already-filled slot; the
   *  shared in-flight set guards against the same slot being enqueued twice; the
   *  ceiling caps the total. Loading an existing deck fires no `slide` events, so
   *  this never runs there. NON-BLOCKING — fire and forget. */
  private enqueueSlideImages(slideId: string): void {
    if (!this.autoFillThisRun || this.autoFillStopped) return;
    this.queueSlideImageSlots(slideId);
  }

  /** Like enqueueSlideImages but for an APPENDED slide (the add-slides path): it
   *  bypasses the FRESH-generation gate (autoFillThisRun is false on an add/refine
   *  turn) because an appended slide is new content the user explicitly asked for,
   *  so it should be imaged like a fresh slide. Still honours autoFillStopped and
   *  the shared queue's other guards (abort, genAllBusy, empty-only, ceiling). */
  private enqueueAddedSlideImages(slideId: string): void {
    if (this.autoFillStopped) return;
    this.queueSlideImageSlots(slideId);
  }

  /** Shared slot-enqueue body for enqueueSlideImages + enqueueAddedSlideImages:
   *  queue a slide's EMPTY image-capable slots for background generation, then
   *  nudge the bounded worker pool. Empty-only is the natural guard against
   *  re-imaging an already-filled slot; the shared in-flight set guards against the
   *  same slot being enqueued twice; the ceiling caps the total. NON-BLOCKING. */
  private queueSlideImageSlots(slideId: string): void {
    if (this.genAllBusy) return;                 // a manual generate-all owns the slots
    if (this.abort?.signal.aborted) return;      // the turn was stopped
    if (!this.deck) return;
    const slide = this.deck.slides.find((s) => s.id === slideId);
    if (!slide) return;
    for (const region of imageSlotsOf(slide)) {
      if (this.autoFillCount >= StudioPage.AUTO_FILL_CEILING) break; // hard ceiling
      if (this.slotImageOf(slide, region)) continue;                 // empty-only
      const key = `${slideId}::${region}`;
      if (this.fillingSlots.has(key)) continue;                      // never twice
      this.fillingSlots.add(key);
      this.autoFillCount += 1;        // count at ENQUEUE so the ceiling bounds the run
      this.autoFillQueue.push({ slideId, region });
    }
    this.pumpAutoFill();
  }

  /** Top the bounded worker pool back up to FILL_CONCURRENCY drainers (a no-op
   *  when already saturated or the queue is empty). Each worker pulls one queued
   *  slot at a time, fills it, and loops until the queue drains or the run aborts. */
  private pumpAutoFill(): void {
    while (this.autoFillWorkers < StudioPage.FILL_CONCURRENCY && this.autoFillQueue.length > 0) {
      this.autoFillWorkers += 1;
      void this.autoFillWorker();
    }
  }

  /** One progressive-fill worker: drain the queue, filling each empty slot via the
   *  shared fillSlot helper, until the queue is empty or the run is aborted /
   *  short-circuited. Soft-fails per slot. The deck is already shown, so a fill
   *  just makes the image pop in. */
  private async autoFillWorker(): Promise<void> {
    try {
      for (;;) {
        if (this.abort?.signal.aborted || this.autoFillStopped) {
          // Aborted or short-circuited: drop the rest of the queue so nothing else
          // fires, releasing each slot's key (a later manual pass may re-fill).
          for (const { slideId, region } of this.autoFillQueue) this.fillingSlots.delete(`${slideId}::${region}`);
          this.autoFillQueue = [];
          return;
        }
        const job = this.autoFillQueue.shift();
        if (!job) return;
        const custom = this.deckImgStyle === 'custom';
        const r = await this.fillSlot(job.slideId, job.region, this.deckImgStyle, custom ? this.deckImgStyleText.trim() : '');
        if (r === 'unconfigured') {
          this.autoFill503s += 1;
          if (this.autoFill503s >= StudioPage.AUTO_FILL_503_LIMIT) {
            // Vertex is down: stop the whole run so we don't hammer it. Drop the
            // queue + release its keys; the loop's guard handles in-flight workers.
            this.autoFillStopped = true;
          }
        }
        // success / soft-failure both just move on to the next slot
      }
    } finally {
      this.autoFillWorkers -= 1;
    }
  }

  /** Shared single-slot fill used by BOTH the progressive per-slide path and the
   *  manual generate-all loop: write an "auto" subject from the slide's context,
   *  generate an image at the region's aspect + the slide's mapped theme + the
   *  chosen style (Custom → styleText), locate the slide BY ID (the deck may have
   *  been re-patched since enqueue), and set the image on it. Returns:
   *    'ok'            — an image was generated + set
   *    'failed'        — a soft failure (bad subject, transient error) — skip
   *    'unconfigured'  — Vertex is unconfigured (503 / ErrNotConfigured) — the
   *                      caller may short-circuit after a few of these
   *    'gone'          — the slide no longer exists (re-patched/removed) — drop
   *  NEVER throws — one failure must not sink the chat or the other slots. */
  private async fillSlot(slideId: string, region: string, style = 'editorial-photo', styleText = ''): Promise<'ok' | 'failed' | 'unconfigured' | 'gone'> {
    if (!this.deck) return 'gone';
    const slide = this.deck.slides.find((s) => s.id === slideId);
    if (!slide) return 'gone';
    const theme = imageThemeFor(this.slideTheme(slide));
    try {
      let subject = '';
      // If the slot already holds the model's own `generate:<subject>` sentinel,
      // honour that crafted subject (and skip the prompt-assist round-trip);
      // otherwise derive a subject from the slide via the "auto" prompt-assist.
      const raw = this.rawSlot(slide, region).trim();
      const sentinel = /^generate:/i.test(raw) ? raw.replace(/^generate:/i, '').trim() : '';
      if (sentinel) {
        subject = sentinel;
      } else {
        try {
          subject = await api.imagePrompt({
            mode: 'auto',
            slide: this.slideTextContext(slide) || undefined,
            deck: this.deckContext() || undefined,
            theme,
          });
        } catch (e) {
          if (e instanceof HttpError && e.status === 503) return 'unconfigured';
          // any other prompt-assist error: fall back to the slide context as subject
        }
      }
      subject = (subject || this.slideTextContext(slide)).trim();
      if (!subject) return 'failed';
      const custom = style === 'custom';
      const res = await api.generateImage({
        subject,
        style: custom ? 'editorial-photo' : style,
        ...(custom && styleText ? { styleText } : {}),
        theme,
        aspect: this.aspectFor(region),
      });
      // Re-resolve by id at SET time — the deck may have been re-patched while the
      // image was generating; if the slide is gone, drop the result.
      const idx = this.deck.slides.findIndex((s) => s.id === slideId);
      if (idx < 0) return 'gone';
      this.setSlotImgAt(idx, region, res.src);
      return 'ok';
    } catch (e) {
      if (e instanceof HttpError && e.status === 503) return 'unconfigured';
      return 'failed';
    }
  }

  // ── structural check→correct pass (Task 5) ─────────────────────────────────
  /** Resolve once the progressive image fill is idle — its queue is empty AND no
   *  workers are draining — so the post-generation self-check measures/corrects a
   *  settled deck rather than racing an in-flight per-slide fill. Polls on a short
   *  interval with a hard safety timeout (a stuck fill must never wedge the check).
   *  Resolves either way; the caller proceeds regardless. */
  private awaitAutoFillIdle(timeoutMs = StudioPage.AUTO_FILL_IDLE_TIMEOUT_MS): Promise<void> {
    return new Promise((resolve) => {
      const idle = () => this.autoFillQueue.length === 0 && this.autoFillWorkers === 0;
      if (idle()) { resolve(); return; }
      const deadline = Date.now() + timeoutMs;
      const tick = () => {
        if (idle() || Date.now() >= deadline) { resolve(); return; }
        setTimeout(tick, 50);
      };
      setTimeout(tick, 50);
    });
  }

  /** Measure ONE slide's content-fit by passing a single-slide deck to measureFit
   *  (the only public surface) — re-using the deck meta + that slide's overrides +
   *  region styles so it lays out exactly as in the canvas. Returns its scale, or
   *  null when the metric is unavailable (no DOM / no report — e.g. the node test
   *  env), so the caller treats "unknown" as "no improvement" rather than crashing. */
  private async measureSlideScale(slide: Slide): Promise<number | null> {
    if (!this.deck) return null;
    const solo: SlideDeck = {
      ...this.deck,
      slides: [slide],
      overrides: { [slide.id]: this.overrides[slide.id] || {} },
      regionStyles: { [slide.id]: this.regionStyles[slide.id] || {} },
    } as SlideDeck;
    const fits = await measureFit(solo);
    return fits.length ? fits[0].scale : null;
  }

  /** Bounded structural self-check after a fresh generation. Three concerns, each
   *  soft (a failure leaves the deck untouched — the chat/deck never breaks):
   *
   *   1. Overpacked slides (scale < OVERPACK_THRESHOLD) → up to CORRECT_ROUNDS
   *      "tighten this slide" edits each, best-so-far (keep an edit only when the
   *      re-measured scale IMPROVES, else revert), bounded to CORRECT_BUDGET slides
   *      per run at CORRECT_CONCURRENCY. Mirrors create-page.ts autoFitRevise's
   *      maxRounds/best-so-far discipline.
   *   2. Image slots still EMPTY after the fill pass → ONE best-effort retry via the
   *      shared fillSlot (deduped on fillingSlots, gated on !genAllBusy, stops on a
   *      503). "Still empty after fill" IS the failed-image signal — no DOM probing.
   *   3. Duplicate layouts → FLAGGED only (console.info); never auto-edited (Task 2
   *      prevents them at source; an edit is an expensive call for a cosmetic issue).
   *
   *  Slides are keyed by id throughout (robust to reorder/re-patch). The whole pass
   *  bails the moment the turn is aborted. */
  private async checkAndCorrect(signal?: AbortSignal): Promise<void> {
    if (!this.deck || signal?.aborted) return;
    // Fresh run: clear the corrected-ids ledger so the visual pass (Task 6) only
    // skips slides THIS pass tightened, not ones from a previous generation.
    this.correctedThisRun = new Set<string>();
    // Don't fight an in-flight per-slide fill: wait for it to drain first.
    await this.awaitAutoFillIdle();
    if (!this.deck || signal?.aborted) return;

    // Measure the settled deck and resolve every FitSlide.index → slide id NOW, so
    // everything downstream keys by id (a correction/fill that re-patches the deck
    // can't shift which slide an index points at).
    let fits: FitSlide[];
    try {
      fits = await measureFit(this.deck);
    } catch {
      return; // metric unavailable — soft no-op
    }
    if (signal?.aborted || !this.deck) return;
    const byId = (i: number): string | undefined => this.deck?.slides[i]?.id;

    // (3) Flag duplicate layouts — purely advisory. A "layout signature" is the
    //     slide kind + its effective layout; ≥2 slides sharing one is worth noting.
    this.flagDuplicateLayouts();

    // (1) Overpacked corrections — collect the offending slide ids (resolved up
    //     front), cap at the budget, then run bounded-concurrently.
    const overpacked: string[] = [];
    for (const f of fits) {
      if (f.scale >= OVERPACK_THRESHOLD) continue;
      const id = byId(f.index);
      if (id) overpacked.push(id);
      if (overpacked.length >= StudioPage.CORRECT_BUDGET) break;
    }
    if (overpacked.length && !signal?.aborted) {
      let next = 0;
      const worker = async (): Promise<void> => {
        for (;;) {
          if (signal?.aborted) return;
          const i = next++;
          if (i >= overpacked.length) return;
          await this.correctOverpackedSlide(overpacked[i], signal);
        }
      };
      const lanes = Math.min(StudioPage.CORRECT_CONCURRENCY, overpacked.length);
      await Promise.all(Array.from({ length: lanes }, () => worker()));
    }
    if (signal?.aborted) return;

    // (2) Failed/empty image retry — one best-effort pass over slots that stayed
    //     empty after the fill. Sequential + gated so it never hammers a dead
    //     backend or fights a manual generate-all.
    await this.retryEmptyImageSlots(signal);
    if (signal?.aborted) return;

    // (4) Advisory "quick visual" review (Task 6) — ONE batched LLM pass that
    //     flags per-slide legibility/visual problems, then applies a bounded
    //     number of single-slide corrections. Flagged + soft-failing; never
    //     re-corrects a slide (1) already tightened.
    await this.visualCheckAndCorrect(signal);
  }

  /** Advisory "quick visual" check→correct pass. After the structural self-check,
   *  ask the server for ONE batched legibility/visual review of the settled deck,
   *  then apply at most VISUAL_CORRECT_BUDGET single-slide corrections (by id):
   *
   *   • gated behind the VISUAL_CHECK_ENABLED flag (default on);
   *   • SOFT-FAIL throughout — a failed review or a failed/odd-shaped edit leaves
   *     the deck unchanged (the chat/deck never breaks);
   *   • each correction is validated by id, re-normalised, and patched by id
   *     (reusing the Task-5 discipline) so a partial AI edit can't crash the canvas;
   *   • a slide already corrected this run (by the overpacked pass OR an earlier
   *     issue here) is SKIPPED, so no slide is double-corrected;
   *   • bails the moment the turn is aborted. */
  private async visualCheckAndCorrect(signal?: AbortSignal): Promise<void> {
    if (!StudioPage.VISUAL_CHECK_ENABLED) return;
    if (!this.deck || signal?.aborted) return;

    let issues: VisualIssue[];
    try {
      issues = await api.visualCheck(this.deck);
    } catch {
      return; // advisory: a failed review leaves the deck untouched
    }
    if (!issues || issues.length === 0 || signal?.aborted || !this.deck) return;

    let applied = 0;
    for (const issue of issues) {
      if (applied >= StudioPage.VISUAL_CORRECT_BUDGET) break;
      if (signal?.aborted) return;
      const id = (issue.slideId || '').trim();
      const fix = (issue.fixInstruction || '').trim();
      if (!id || !fix) continue;
      if (this.correctedThisRun.has(id)) continue; // never double-correct a slide
      const slide = this.deck.slides.find((s) => s.id === id);
      if (!slide) continue; // server already drops unknown ids; defend anyway
      // Reserve the id BEFORE the edit so a re-flagged slide isn't queued twice.
      this.correctedThisRun.add(id);
      let candidate: Slide;
      try {
        const parsed = (await api.editSlide(slide, fix)) as unknown;
        // Validate the edit kept the slide's identity (same guard as Task 5).
        const ok = !!parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          && (parsed as { id?: unknown }).id === id;
        if (!ok) continue; // odd shape — leave the slide as-is
        candidate = normaliseSlide(parsed as Slide); // re-normalise BEFORE patching
      } catch {
        continue; // edit failed — leave the slide unchanged
      }
      if (signal?.aborted) return;
      this.patchSlideById(id, candidate);
      applied += 1;
    }
  }

  /** Tighten ONE overpacked slide, best-so-far. Up to CORRECT_ROUNDS edits; each
   *  edit is KEPT only when the re-measured content-fit scale strictly improves,
   *  otherwise reverted to the best version seen. The slide is re-normalised before
   *  every patch (so a partial AI edit can't re-crash the canvas — Task 1) and
   *  located BY ID each round (robust to a concurrent re-patch). Soft-fails:
   *  any error or unmeasurable round leaves the best slide in place. */
  private async correctOverpackedSlide(slideId: string, signal?: AbortSignal): Promise<void> {
    if (!this.deck) return;
    let best = this.deck.slides.find((s) => s.id === slideId) || null;
    if (!best) return;
    let bestScale = await this.measureSlideScale(best);
    // No metric (e.g. no DOM) → we can't tell improvement from regression, so
    // best-so-far would never accept an edit. Skip rather than blindly apply one.
    if (bestScale == null) return;

    const instruction =
      'Tighten this slide so its content fits on one slide without overflowing — ' +
      'cut/condense words, never add.';

    for (let round = 0; round < StudioPage.CORRECT_ROUNDS; round++) {
      if (signal?.aborted) return;
      if (bestScale >= OVERPACK_THRESHOLD) break; // already fits — stop early
      let candidate: Slide;
      try {
        const parsed = (await api.editSlide(best, instruction)) as unknown;
        // Validate the edit kept the slide's identity, exactly like applyCardEdit.
        const ok = !!parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          && (parsed as { id?: unknown }).id === slideId;
        if (!ok) return; // unexpected shape — keep the best we have
        candidate = normaliseSlide(parsed as Slide); // re-normalise BEFORE measuring/patching
      } catch {
        return; // edit failed — keep the best version
      }
      if (signal?.aborted) return;
      const candScale = await this.measureSlideScale(candidate);
      // Keep the edit ONLY when it strictly improves the scale; otherwise the best
      // version stays in place (best-so-far — a correction never makes a slide worse).
      if (candScale != null && candScale > bestScale) {
        best = candidate;
        bestScale = candScale;
        this.patchSlideById(slideId, candidate);
        // Record so the advisory visual pass (Task 6) never re-edits this slide.
        this.correctedThisRun.add(slideId);
      } else {
        // No improvement: stop spending rounds on this slide.
        break;
      }
    }
  }

  /** Patch one slide BY ID into the deck (the AI's correction supersedes any inline
   *  override on it), repaint, and schedule a save — the same discipline as the
   *  streamed-slide path. No-op if the slide is gone (re-patched/removed). */
  private patchSlideById(slideId: string, slide: Slide): void {
    if (!this.deck) return;
    const idx = this.deck.slides.findIndex((s) => s.id === slideId);
    if (idx < 0) return;
    this.deck = { ...this.deck, slides: this.deck.slides.map((s) => (s.id === slideId ? slide : s)) };
    this.clearOverrides([slideId]);
    this.focusKey = ''; // repaint the focus editor if it's this slide
    this.scheduleSave();
  }

  /** Image slots still empty after the fill pass are the "failed/empty image"
   *  signal. Retry each ONCE via the shared fillSlot — deduped on fillingSlots (so
   *  it never collides with the per-slide fill or generate-all), gated on
   *  !genAllBusy, and stopped the moment a 503 says the image backend is down.
   *  Sequential + best-effort; resolved by slide id so a re-patch can't mis-target. */
  private async retryEmptyImageSlots(signal?: AbortSignal): Promise<void> {
    if (!this.deck || signal?.aborted || this.genAllBusy) return;
    // Snapshot the empty slots BY ID up front (resolve index → id once).
    const jobs: { slideId: string; region: string }[] = [];
    for (const slide of this.deck.slides) {
      for (const region of imageSlotsOf(slide)) {
        if (this.slotImageOf(slide, region)) continue; // already filled
        jobs.push({ slideId: slide.id, region });
      }
    }
    const custom = this.deckImgStyle === 'custom';
    const styleText = custom ? this.deckImgStyleText.trim() : '';
    for (const { slideId, region } of jobs) {
      if (signal?.aborted || this.genAllBusy) return;
      const key = `${slideId}::${region}`;
      if (this.fillingSlots.has(key)) continue; // in flight elsewhere — don't double-fill
      this.fillingSlots.add(key);
      try {
        const r = await this.fillSlot(slideId, region, this.deckImgStyle, styleText);
        if (r === 'unconfigured') return; // backend down — stop retrying the rest
      } catch {
        /* soft-fail: a failed retry leaves the slot empty */
      } finally {
        this.fillingSlots.delete(key); // release the reservation (one retry only)
      }
    }
  }

  /** Flag (advisory only) slides that share a layout signature (kind + effective
   *  layout). Task 2 prevents duplicates at generation source; auto-editing them
   *  would be an expensive model call for a cosmetic issue, so this only logs. */
  private flagDuplicateLayouts(): void {
    if (!this.deck) return;
    const seen = new Map<string, number>();
    for (const slide of this.deck.slides) {
      const sig = `${slide.kind}/${this.curLayout(slide)}`;
      seen.set(sig, (seen.get(sig) || 0) + 1);
    }
    const dups = [...seen.entries()].filter(([, n]) => n > 1);
    if (dups.length) {
      // eslint-disable-next-line no-console
      console.info('[deck self-check] duplicate layouts:', dups.map(([sig, n]) => `${sig}×${n}`).join(', '));
    }
  }

  /** Collect the image slots across the deck. When `includeFilled` is on every
   *  slot is a target (regenerate the whole deck); otherwise only the empty ones
   *  (no real image — a gradient counts as empty). */
  private collectImageSlots(includeFilled: boolean): { slideIndex: number; region: string }[] {
    const out: { slideIndex: number; region: string }[] = [];
    (this.deck?.slides || []).forEach((slide, slideIndex) => {
      for (const region of imageSlotsOf(slide)) {
        if (includeFilled || !this.slotImageOf(slide, region)) out.push({ slideIndex, region });
      }
    });
    return out;
  }

  /** "Generate images" (deck-level): open a small options dialog — pick the art
   *  style (defaulting from the deck's preference) and whether to replace images
   *  that already exist — then run the fill. */
  private generateAllImages(): void {
    if (!this.deck || this.genAllBusy) return;
    this.genNotice = null;
    // Seed the options from the deck-level preference; replace defaults OFF.
    this.genAllStyle = this.deckImgStyle;
    this.genAllStyleText = this.deckImgStyleText;
    this.genAllReplace = false;
    this.genAllOpen = true;
  }

  /** The friendly name of an IMG_STYLE_PRESETS id (for the confirm/summary copy). */
  private styleLabel(id: string): string {
    return IMG_STYLE_PRESETS.find((s) => s.id === id)?.label || 'Photo';
  }

  /** Run generate-all with the chosen options: resolve the targets (empty-only or
   *  every slot when "replace existing" is on), cap the list, persist the chosen
   *  style as the deck preference, then fill. */
  private confirmGenerateAll(): void {
    if (!this.deck || this.genAllBusy) return;
    const all = this.collectImageSlots(this.genAllReplace);
    if (all.length === 0) {
      this.genAllOpen = false;
      this.genNotice = { msg: this.genAllReplace ? 'This deck has no image slots to fill.' : 'Every image slot in this deck already has an image.' };
      return;
    }
    // The chosen style becomes the deck's preference, so later single-slot
    // generation stays consistent with the deck-wide pass.
    this.deckImgStyle = this.genAllStyle;
    this.deckImgStyleText = this.genAllStyleText;
    const cap = StudioPage.GEN_ALL_CAP;
    const targets = all.slice(0, cap);
    const overflow = all.length - targets.length;
    this.genAllOpen = false;
    void this.runGenerateAll(targets, overflow, this.genAllStyle, this.genAllStyle === 'custom' ? this.genAllStyleText.trim() : '');
  }

  /** The generate-all options dialog: an art-style picker (Custom → a text field),
   *  a "Replace existing images" toggle, and a Generate button whose copy
   *  reflects the count + style. */
  private renderGenAllDialog(): TemplateResult {
    const custom = this.genAllStyle === 'custom';
    const all = this.collectImageSlots(this.genAllReplace);
    const cap = StudioPage.GEN_ALL_CAP;
    const n = Math.min(all.length, cap);
    const styleName = this.styleLabel(this.genAllStyle);
    const verb = this.genAllReplace ? 'Replace' : 'Generate';
    const cta = n === 0
      ? 'No slots to fill'
      : `${verb} ${n} image${n === 1 ? '' : 's'} in the ${styleName} style`;
    return html`
      <div class="scrim" @mousedown=${(e: MouseEvent) => { if (e.target === e.currentTarget) this.genAllOpen = false; }}>
        <div class="confirm-dialog genall-dialog" role="dialog" aria-modal="true" aria-label="Generate images across the deck" @click=${(e: Event) => e.stopPropagation()}>
          <div class="genall-title">Generate images across the deck</div>
          <div class="genall-sub faint">Pick one art style so the whole deck's imagery feels consistent.</div>
          <div class="imgp-label">Art style</div>
          <div class="imgp-styles">
            ${IMG_STYLE_PRESETS.map((s) => html`
              <button class="imgp-style ${this.genAllStyle === s.id ? 'on' : ''}" aria-pressed=${this.genAllStyle === s.id}
                @click=${() => { this.genAllStyle = s.id; }}>${s.label}</button>`)}
          </div>
          ${custom ? html`
            <input class="imgp-subj imgp-styletext" placeholder="Describe the art style…" .value=${this.genAllStyleText}
              @input=${(e: Event) => { this.genAllStyleText = (e.target as HTMLInputElement).value; }}>` : nothing}
          <label class="imgp-toggle genall-replace">
            <input type="checkbox" .checked=${this.genAllReplace}
              @change=${(e: Event) => { this.genAllReplace = (e.target as HTMLInputElement).checked; }}>
            <span>Replace existing images <span class="faint">— regenerate slots that already have one</span></span>
          </label>
          ${all.length > cap ? html`<div class="genall-cap faint">${all.length} slots match — the first ${cap} will be filled this run.</div>` : nothing}
          <div class="confirm-actions">
            <button class="btn btn--ghost btn--sm" @click=${() => { this.genAllOpen = false; }}>Cancel</button>
            <button class="btn btn--ai btn--sm" ?disabled=${n === 0} @click=${() => this.confirmGenerateAll()}>${cta}</button>
          </div>
        </div>
      </div>`;
  }

  // ── pre-generation deck-style panel (the empty / generating canvas) ────────
  /** Pick an up-front THEME. Stored in preThemeChoice; applied to the AI's deck
   *  once it arrives (the user's explicit choice beats the model's). If a deck
   *  already exists (e.g. an empty draft), apply it live too. */
  private setPreTheme(id: string): void {
    this.preThemeChoice = this.preThemeChoice === id ? null : id;
    if (this.preThemeChoice && this.deck) this.applyBundle(this.preThemeChoice);
  }

  /** Pick an up-front IMAGE style. Seeds deckImgStyle so every later generation
   *  (single-slot, generate-all) leans on it. Toggling the active preset off
   *  reverts to the house default. */
  private setPreImgStyle(id: string): void {
    if (this.deckImgStyle === id && id !== 'custom') {
      this.deckImgStyle = 'editorial-photo';
      this.deckImgStyleText = '';
      return;
    }
    this.deckImgStyle = id;
    if (id !== 'custom') this.deckImgStyleText = '';
  }

  /** Apply the user's up-front theme pick to a freshly-generated deck, overriding
   *  whatever theme the AI chose. Called from every deck-arrival path (streamed
   *  outline, non-streamed envelope). Clears the pending choice once applied so a
   *  later AI turn doesn't get re-overridden. */
  private applyPreThemeChoice(): void {
    if (!this.preThemeChoice || !this.deck) return;
    const pick = this.preThemeChoice;
    this.preThemeChoice = null;
    this.applyBundle(pick); // sets theme + fontset + brand via bundleFor, applies + persists
  }

  /** Whether the canvas should show the "Building your deck" animation: a FRESH
   *  generation is in flight (this.building), set for the duration of a first-gen
   *  turn and cleared once every outline slide has streamed in. A refine turn
   *  never sets it, so an existing deck keeps its real canvas + per-slide
   *  shimmer. */
  private isBuilding(): boolean {
    return this.building && this.loading;
  }

  /** The "Building your deck" animation shown in the canvas while a generation
   *  is in flight — REPLACES the pre-theme picker (which makes no sense to stare
   *  at while the deck is being built). An animated skeleton deck (a cover plus
   *  a few content cards) shimmers under a soft progress line, with a live
   *  caption that tracks the stream: "Planning the outline…" before the outline
   *  lands, then "Designing your slides… / Slide N of M" as slides arrive. The
   *  counts come straight from the stream state the studio already tracks — the
   *  outline seeds this.pending with every slide id, and each landed slide
   *  removes its id, so filled = total − pending.size. Once REAL slides exist
   *  the `deck.slides.length` branch above takes over the canvas and this is no
   *  longer rendered, so the two never double-show. Pure CSS animation; the
   *  reduced-motion fallback is handled in studio-page.css. */
  private renderBuildingDeck(): TemplateResult {
    // Before the outline event the deck is an empty 0-slide draft (ensureDeck);
    // once it lands, deck.slides.length is the planned total and this.pending
    // holds the not-yet-filled ids. Either way the renderer is only reached
    // while the deck has no rendered slides, so these counts are stream-only.
    const total = this.deck?.slides.length ?? 0;
    const filled = Math.max(0, total - this.pending.size);
    // serverCaption drives the PRE-outline caption from the real backend phase;
    // currentTitle (the latest landed slide's title) drives the streaming detail.
    const ph = buildingPhase({ total, filled, serverCaption: this.buildingStatus, currentTitle: this.buildingTitle });
    const pct = Math.round(ph.progress * 100);
    // A cover plus three content cards reads as "a deck taking shape" without
    // implying a specific slide count before the outline declares one.
    const cards = [0, 1, 2, 3];
    return html`
      <div class="canvas-empty canvas-building" role="status" aria-busy="true"
        aria-label=${ph.detail ? `${ph.caption} ${ph.detail}` : ph.caption}>
        <div class="building-stage" aria-hidden="true">
          ${cards.map((i) => html`
            <div class="bld-card ${i === 0 ? 'bld-card--cover' : 'bld-card--content'}" style="--bld-i:${i}">
              ${i === 0
                ? html`
                    <span class="bld-line bld-line--eyebrow"></span>
                    <span class="bld-line bld-line--title"></span>
                    <span class="bld-line bld-line--title is-short"></span>
                    <span class="bld-line bld-line--sub"></span>`
                : html`
                    <span class="bld-line bld-line--head"></span>
                    <span class="bld-line bld-line--body"></span>
                    <span class="bld-line bld-line--body is-short"></span>`}
            </div>`)}
        </div>
        <div class="building-meta">
          <p class="building-caption">${ph.caption}</p>
          <div class="building-progress" role="presentation">
            <span class="building-progress-fill ${ph.phase === 'planning' ? 'is-indeterminate' : ''}"
              style=${ph.phase === 'planning' ? '' : `width:${pct}%`}></span>
          </div>
          ${ph.detail ? html`<p class="building-detail faint">${ph.detail}</p>` : nothing}
          ${this.buildingThinking
            ? html`<div class="building-thinking faint" aria-live="polite">${this.buildingThinking}</div>`
            : nothing}
        </div>
      </div>`;
  }

  /** The optional "Deck style" panel shown on the empty / generating canvas:
   *  THEME swatches + IMAGE-STYLE presets. Purely the user's up-front
   *  preferences — the deck is still driven by the chat brief on the left. */
  private renderPreGenPanel(): TemplateResult {
    const curTheme = this.preThemeChoice
      || (this.deck?.meta.theme && THEME_IDS.includes(this.deck.meta.theme) ? this.deck.meta.theme : '');
    const custom = this.deckImgStyle === 'custom';
    return html`
      <div class="pregen-panel" @click=${(e: Event) => e.stopPropagation()}>
        <div class="pregen-panel-head">Deck style</div>
        <div class="pregen-panel-hint faint">Optional — pick a look, or just describe your deck on the left.</div>
        <div class="pregen-label">Theme</div>
        <div class="pregen-themes">
          ${THEME_BUNDLES.map((b) => html`
            <button class="pregen-theme ${curTheme === b.id ? 'on' : ''}" aria-pressed=${curTheme === b.id} title=${b.blurb}
              @click=${() => this.setPreTheme(b.id)}>
              <span class="pregen-chip ${b.dark ? 'is-dark' : 'is-light'}" style="--c:${b.accent}"></span>
              <span class="pregen-theme-name">${b.label}</span>
            </button>`)}
        </div>
        <div class="pregen-label">Image style</div>
        <div class="imgp-styles pregen-styles">
          ${IMG_STYLE_PRESETS.map((s) => html`
            <button class="imgp-style ${this.deckImgStyle === s.id ? 'on' : ''}" aria-pressed=${this.deckImgStyle === s.id}
              @click=${() => this.setPreImgStyle(s.id)}>${s.label}</button>`)}
        </div>
        ${custom ? html`
          <input class="imgp-subj imgp-styletext pregen-styletext" placeholder="Describe the art style…" .value=${this.deckImgStyleText}
            @input=${(e: Event) => { this.deckImgStyleText = (e.target as HTMLInputElement).value; }}>` : nothing}
      </div>`;
  }

  /** Bounded-parallel (concurrency 3) fill of the chosen slots, in one chosen art
   *  style. Each target is resolved to a stable slide id up front (so a fill
   *  re-finds its slide by id even if the deck is re-patched mid-pass) and routed
   *  through the shared fillSlot helper — the SAME helper + shared in-flight set
   *  the progressive per-slide path uses, so the two can never double-fill a slot.
   *  Each target is independently handled so one failure never sinks the batch; a
   *  summary is shown at the end. */
  private async runGenerateAll(targets: { slideIndex: number; region: string }[], overflow: number, style = 'editorial-photo', styleText = ''): Promise<void> {
    if (!this.deck || targets.length === 0) return;
    // Resolve each index target to a stable slide id NOW, and reserve it in the
    // shared in-flight set so the progressive path won't also pick it up. Any slot
    // already reserved (in flight from the per-slide fill) is skipped.
    const slides = this.deck.slides;
    const jobs: { slideId: string; region: string; key: string; label: string }[] = [];
    for (const { slideIndex, region } of targets) {
      const slide = slides[slideIndex];
      if (!slide) continue;
      const key = `${slide.id}::${region}`;
      if (this.fillingSlots.has(key)) continue; // already in flight elsewhere
      this.fillingSlots.add(key);
      jobs.push({ slideId: slide.id, region, key, label: `“${this.stripHtml((slide as { title?: string }).title || slide.kind || 'slide')}”` });
    }
    if (jobs.length === 0) return;
    this.genAllBusy = true;
    this.error = '';
    this.genNotice = null;
    this.genAllProgress = { done: 0, total: jobs.length };
    const failed: string[] = [];
    let done = 0;
    const concurrency = StudioPage.FILL_CONCURRENCY;
    let next = 0;
    const worker = async (): Promise<void> => {
      for (;;) {
        const i = next++;
        if (i >= jobs.length) return;
        const { slideId, region, key, label } = jobs[i];
        try {
          const r = await this.fillSlot(slideId, region, style, styleText);
          if (r === 'failed' || r === 'unconfigured') failed.push(label);
          // 'gone' (slide removed) silently drops; 'ok' succeeds
        } finally {
          this.fillingSlots.delete(key); // release the reservation
          done += 1;
          this.genAllProgress = { done, total: jobs.length };
        }
      }
    };
    try {
      await Promise.all(Array.from({ length: Math.min(concurrency, jobs.length) }, () => worker()));
    } finally {
      this.genAllBusy = false;
      const ok = jobs.length - failed.length;
      const parts: string[] = [`Generated ${ok} of ${jobs.length} image${jobs.length === 1 ? '' : 's'}.`];
      if (failed.length) parts.push(`${failed.length} failed (${failed.slice(0, 4).join(', ')}${failed.length > 4 ? '…' : ''}).`);
      if (overflow > 0) parts.push(`${overflow} more empty slot${overflow === 1 ? '' : 's'} left — run again to fill them.`);
      this.genNotice = { msg: parts.join(' '), error: failed.length === jobs.length };
    }
  }

  /** Lazy-load the owner's image gallery once for the "Choose existing" tab. */
  private async loadGallery(): Promise<void> {
    if (this.galleryLoaded || this.galleryLoading) return;
    this.galleryLoading = true; this.error = '';
    try {
      this.galleryImgs = await api.listImages();
      this.galleryLoaded = true;
    } catch (e) {
      this.error = e instanceof HttpError ? `Couldn’t load your images (${e.status}).` : 'Couldn’t load your images.';
    } finally {
      this.galleryLoading = false;
    }
  }

  /** Switch the image popover tab; loads the gallery the first time "pick" opens. */
  private setImgTab(tab: 'gen' | 'pick'): void {
    this.imgTab = tab;
    if (tab === 'pick') void this.loadGallery();
  }

  /** Pick an existing gallery image for the current slot, then close. */
  private pickGalleryImg(region: string, src: string): void {
    this.setSlotImg(region, src);
    this.imgSel = null;
  }

  /** Upload an image for a slot — embedded as a data URL (self-contained deck). */
  private async onSlotUpload(e: Event, region: string): Promise<void> {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || this.focusIndex == null) return;
    this.error = '';
    if (file.size > 6 * 1024 * 1024) {
      this.error = `That image is ${(file.size / 1048576).toFixed(1)} MB — please keep slide images under 6 MB.`;
      input.value = ''; return;
    }
    this.uploading = file.name;
    try {
      const dataUrl = await fileToDataUrl(file);
      if (this.deckId) {
        try {
          const b64 = dataUrl.split(',')[1] || '';
          await api.uploadSource(this.deckId, { filename: file.name, mime: file.type || 'image/png', contentBase64: b64 });
        } catch { /* the embedded data URL is the source of truth */ }
      }
      this.setSlotImg(region, dataUrl);
      this.imgSel = null;
    } catch {
      this.error = 'Couldn’t read that image.';
    } finally {
      this.uploading = '';
      input.value = '';
    }
  }

  /** The click-to-edit image popover: a Generate tab (AI prompt-assist + style
   *  presets + theme + gradient + upload + remove) and a Choose-existing tab
   *  (the owner's image gallery). */
  private renderImgPop(): TemplateResult {
    const region = this.imgSel!.region;
    const p = this.popPos(320, 420);
    return html`
      <div class="pop img-pop" role="menu" aria-label="Change image"
        style="position:fixed;left:${p.left}px;top:${p.top}px" @click=${(e: Event) => e.stopPropagation()}>
        <div class="imgp-tabs" role="tablist" aria-label="Image source">
          <button class="imgp-tab ${this.imgTab === 'gen' ? 'on' : ''}" role="tab" aria-selected=${this.imgTab === 'gen'}
            @click=${() => this.setImgTab('gen')}>Generate</button>
          <button class="imgp-tab ${this.imgTab === 'pick' ? 'on' : ''}" role="tab" aria-selected=${this.imgTab === 'pick'}
            @click=${() => this.setImgTab('pick')}>Choose existing</button>
        </div>
        ${this.imgTab === 'gen' ? this.renderImgGen(region) : this.renderImgPick(region)}
      </div>`;
  }

  /** The Generate tab — prompt-assist, style presets, theme toggle, describe +
   *  Generate, gradient gallery, upload and remove. */
  private renderImgGen(region: string): TemplateResult {
    const cur = this.curSlot(region);
    const has = !!(cur.img || cur.art);
    const custom = this.imgStyle === 'custom';
    const hasSubject = !!this.imgGenSubject.trim();
    return html`
      <div class="imgp-body">
        <div class="imgp-label">Art style</div>
        <div class="imgp-styles">
          ${IMG_STYLE_PRESETS.map((s) => html`
            <button class="imgp-style ${this.imgStyle === s.id ? 'on' : ''}" aria-pressed=${this.imgStyle === s.id}
              @click=${() => { this.imgStyle = s.id; }}>${s.label}</button>`)}
        </div>
        ${custom ? html`
          <input class="imgp-subj imgp-styletext" placeholder="Describe the art style…" .value=${this.imgStyleText}
            @input=${(e: Event) => { this.imgStyleText = (e.target as HTMLInputElement).value; }}>` : nothing}
        <label class="imgp-toggle">
          <input type="checkbox" .checked=${this.imgUseTheme}
            @change=${(e: Event) => { this.imgUseTheme = (e.target as HTMLInputElement).checked; }}>
          <span>Use theme style</span>
        </label>
        <div class="imgp-label">Describe</div>
        <div class="imgp-assist">
          <button class="imgp-assist-btn" ?disabled=${this.imgBusy}
            @click=${() => void this.autoImgPrompt()}>✨ Auto</button>
          <button class="imgp-assist-btn" ?disabled=${this.imgBusy || !hasSubject}
            @click=${() => void this.enhanceImgPrompt()}>Enhance prompt</button>
        </div>
        <div class="imgp-gen">
          <input class="imgp-subj" placeholder="Describe an image to generate…" .value=${this.imgGenSubject}
            ?disabled=${this.imgBusy}
            @input=${(e: Event) => { this.imgGenSubject = (e.target as HTMLInputElement).value; }}
            @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' && this.imgGenSubject.trim()) void this.genSlotImg(region); }}>
          <button class="btn btn--sm" ?disabled=${this.imgBusy || !hasSubject}
            @click=${() => void this.genSlotImg(region)}>${this.imgBusy ? 'Generating…' : 'Generate'}</button>
        </div>
        <div class="imgp-label">Or pick a gradient</div>
        <div class="bg-grid">
          ${ART_OPTIONS.map((a) => html`
            <button class="bg-sw bg-art-${a.id} ${cur.art === a.id ? 'on' : ''}" title=${a.name} aria-label=${a.name}
              @click=${() => this.setSlotArt(region, a.id)}></button>`)}
        </div>
        <div class="imgp-actions">
          <label class="imgp-upload">
            <input type="file" hidden accept="image/png,image/jpeg,image/webp,image/avif" @change=${(e: Event) => void this.onSlotUpload(e, region)}>
            ${this.uploading ? 'Uploading…' : '⬆ Upload'}
          </label>
          ${has ? html`<button class="imgp-clear" @click=${() => this.clearSlot(region)}>Remove</button>` : nothing}
        </div>
      </div>`;
  }

  /** The Choose-existing tab — a lazy-loaded thumbnail grid of the owner's
   *  generated images; clicking one sets the slot and closes the popover. */
  private renderImgPick(region: string): TemplateResult {
    if (this.galleryLoading) {
      return html`<div class="imgp-body imgp-gallery-state"><span class="up-spin"></span></div>`;
    }
    if (!this.galleryImgs.length) {
      return html`<div class="imgp-body imgp-gallery-state imgp-gallery-empty">No images yet — generate one above.</div>`;
    }
    return html`
      <div class="imgp-body">
        <div class="imgp-gallery">
          ${this.galleryImgs.map((g) => html`
            <button class="imgp-thumb" title=${g.subject || 'Untitled'}
              @click=${() => this.pickGalleryImg(region, g.src)}>
              <img src=${g.src} alt=${g.subject || 'Untitled'} loading="lazy">
            </button>`)}
        </div>
      </div>`;
  }

  /** Plain-text projection of a (possibly rich) note, for the "has notes" dot. */
  private stripHtml(s: string): string {
    if (!s) return '';
    const d = document.createElement('div'); d.innerHTML = s; return d.textContent || '';
  }

  private popPos(w: number, hgt: number): { left: number; top: number } {
    const r = this.selRect || { left: 200, top: 200, bottom: 240 };
    const vw = window.innerWidth, vh = window.innerHeight;
    const left = Math.min(Math.max(r.left, 16), vw - w - 16);
    let top = r.bottom + 8;
    if (top + hgt > vh - 16) top = Math.max(16, r.top - hgt - 8);
    return { left, top };
  }

  // ── "Edit this card" AI popover ────────────────────────────────────────
  /** Open the card-edit popover from the toolbar button, anchoring it to the
   *  button's rect (so popPos places it just below the toolbar). */
  private openCardEdit(e: Event): void {
    if (!this.focusSlide) return;
    const btn = e.currentTarget as HTMLElement | null;
    if (btn) { const r = btn.getBoundingClientRect(); this.selRect = { left: r.left, top: r.top, bottom: r.bottom }; }
    // close the sibling popovers/menus so only this one is open
    this.sel = null; this.imgSel = null; this.diff = null; this.toolMenu = null;
    this.cardEditVal = '';
    this.cardEdit = true;
  }

  /** Open the image popover on the slide's background slot (the "Add image"
   *  quick action). Mirrors the wireRegions image-slot open path. */
  private openCardAddImage(): void {
    this.cardEdit = false;
    this.imgGenSubject = '';
    this.imgTab = 'gen';
    // Seed the per-open style from the deck-level preference (see wireRegions).
    this.imgStyle = this.deckImgStyle;
    this.imgStyleText = this.deckImgStyleText;
    this.imgSel = { region: 'bg' };
  }

  /** Open the layout menu (the "Try new layout" quick action). */
  private openCardLayout(): void {
    this.cardEdit = false;
    this.toolMenu = 'layout';
  }

  /** Pre-fill the instruction with a translate stub and focus the input so the
   *  user types the target language (the "Translate" quick action). */
  private translateCard(): void {
    this.cardEditVal = 'Translate this slide to ';
    this.updateComplete.then(() => {
      const input = this.querySelector<HTMLInputElement>('.card-edit-pop .ce-input');
      if (input) { input.focus(); input.setSelectionRange(input.value.length, input.value.length); }
    });
  }

  /** Apply a free-text AI edit to the FOCUSED slide only.
   *
   *  Correctness-critical: the server's edit-slide op returns ONE rewritten
   *  slide (preserving id + kind). We parse it, validate it is an object whose
   *  id matches the focused slide, and patch ONLY that slide via mutateSlides.
   *  On ANY failure (empty instruction, parse error, non-object, id mismatch)
   *  the deck is left UNTOUCHED and a friendly error is surfaced — we never
   *  replace the deck or another slide. */
  private async applyCardEdit(instruction: string): Promise<void> {
    const instr = instruction.trim();
    const slide = this.focusSlide;
    const idx = this.focusIndex;
    if (!slide || idx == null || !instr || this.cardBusy) return;
    this.cardBusy = true;
    this.error = '';
    this.aiBusy = new Set([slide.id]); // shimmer the focused slide while it works
    try {
      const parsed = (await api.editSlide(slide, instr)) as unknown;
      const ok = !!parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        && (parsed as { id?: unknown }).id === slide.id;
      if (!ok) {
        this.error = 'The AI edit came back in an unexpected shape — your card is unchanged.';
        return; // leave the deck UNTOUCHED
      }
      const next = parsed as Slide;
      // Patch ONLY the focused slide. NEVER replace the whole deck.
      this.mutateSlides((s) => { s[idx] = next; });
      this.clearOverrides([slide.id]); // the AI's new slide supersedes any inline edits
      this.focusKey = ''; // repaint the focus editor with the edited slide
      this.cardEdit = false;
      this.cardEditVal = '';
    } catch (e) {
      this.error = e instanceof HttpError ? `Card edit failed (${e.status}).` : 'Card edit failed.';
    } finally {
      this.cardBusy = false;
      this.aiBusy = new Set();
    }
  }

  /** The "Edit this card" popover: a free-text instruction plus quick actions.
   *  Mirrors renderEditPop / renderImgPop styling — a .pop at popPos. */
  private renderCardEditPop(): TemplateResult {
    const p = this.popPos(340, 280);
    const has = !!this.cardEditVal.trim();
    return html`
      <div class="pop card-edit-pop" role="dialog" aria-label="Edit this card with AI"
        style="position:fixed;left:${p.left}px;top:${p.top}px" @click=${(e: Event) => e.stopPropagation()}>
        <div class="ce-head">✦ Edit this card</div>
        <div class="ce-in">
          <input class="ce-input" placeholder="How would you like to edit this card?" .value=${this.cardEditVal}
            ?disabled=${this.cardBusy}
            @input=${(e: Event) => { this.cardEditVal = (e.target as HTMLInputElement).value; }}
            @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' && this.cardEditVal.trim()) void this.applyCardEdit(this.cardEditVal); }}>
          <button class="send-btn" aria-label="Send" ?disabled=${this.cardBusy || !has}
            @click=${() => void this.applyCardEdit(this.cardEditVal)}>${this.cardBusy ? html`<span class="up-spin"></span>` : '↑'}</button>
        </div>
        <div class="ce-label">Quick actions</div>
        <div class="ce-chips">
          <button class="ce-chip" ?disabled=${this.cardBusy} @click=${() => void this.applyCardEdit('Improve the writing and clarity of this slide')}>Improve writing</button>
          <button class="ce-chip" ?disabled=${this.cardBusy} @click=${() => void this.applyCardEdit('Make this slide more concise')}>Make shorter</button>
          <button class="ce-chip" ?disabled=${this.cardBusy} @click=${() => void this.applyCardEdit('Make this slide more visual — prefer an image-led layout where it fits')}>Make more visual</button>
          <button class="ce-chip" ?disabled=${this.cardBusy} @click=${() => this.translateCard()}>Translate</button>
          <button class="ce-chip" ?disabled=${this.cardBusy} @click=${() => this.openCardAddImage()}>Add image</button>
          <button class="ce-chip" ?disabled=${this.cardBusy} @click=${() => this.openCardLayout()}>Try new layout</button>
        </div>
      </div>`;
  }

  // ── inline rich-text floating toolbar ──────────────────────────────────
  /** Apply an inline mark to the live selection via execCommand (the pragmatic
   *  path for contentEditable). Whatever the browser emits (<b>/<i>/<u>/<strike>/
   *  <font>/style=…) is normalised on commit by sanitizeRich(innerHTML) into the
   *  allowlist, so this never widens the XSS surface. mousedown+preventDefault
   *  keeps the selection (clicking a button would otherwise blur the editable).
   *  After the command we re-commit the region so the override updates live. */
  private rtCmd(e: MouseEvent, cmd: 'bold' | 'italic' | 'underline' | 'strikethrough'): void {
    e.preventDefault();
    const node = this.rtEditNode();
    if (!node) return;
    document.execCommand(cmd, false);
    this.rtRecommit(node);
  }

  /** The contenteditable node currently being inline-edited (the rich region the
   *  toolbar is bound to), or null if none. */
  private rtEditNode(): HTMLElement | null {
    if (!this.rtBar) return null;
    const node = this.querySelector<HTMLElement>(`.focus-host [data-region="${this.rtBar.region}"]`);
    return node && node.isContentEditable ? node : null;
  }

  /** Re-commit the region's sanitised innerHTML into overrides (the live commit
   *  path also fires on input, but execCommand/colour don't always emit an input
   *  event, so we commit explicitly). */
  private rtRecommit(node: HTMLElement): void {
    const slide = this.focusSlide;
    if (!slide || !this.rtBar) return;
    const value = sanitizeRich(node.innerHTML);
    this.overrides = { ...this.overrides, [slide.id]: { ...(this.overrides[slide.id] || {}), [this.rtBar.region]: value } };
    this.scheduleSave();
  }

  /** Wrap the selection in an `<a href>` (prompt for the URL). The sanitiser
   *  enforces the safe-scheme allowlist on render, so a hostile scheme is dropped
   *  even though we accept the raw input here. */
  private rtLink(e: MouseEvent): void {
    e.preventDefault();
    const node = this.rtEditNode();
    if (!node) return;
    const url = window.prompt('Link URL (https://…):', 'https://');
    if (url == null) return;
    const u = url.trim();
    if (!u) { document.execCommand('unlink', false); this.rtRecommit(node); return; }
    document.execCommand('createLink', false, u);
    this.rtRecommit(node);
  }

  /** Colour the selection by wrapping it in a `<span style="color:…">`. We use
   *  execCommand('foreColor') for the selection wrap; sanitizeRich keeps only the
   *  colour-only span (mapping any `<font color>` execCommand emits). `hex` is a
   *  concrete colour (the Paper-derived brand palette) so it survives the
   *  colour-only sanitiser, which rejects var()/url()/expression(). */
  private rtColour(e: MouseEvent, hex: string): void {
    e.preventDefault();
    const node = this.rtEditNode();
    if (!node) return;
    document.execCommand('foreColor', false, hex);
    this.rtColourOpen = false;
    this.rtRecommit(node);
  }

  /** Set the heading size for the WHOLE region (not the selection) — writes
   *  regionStyles via setRegionStyle. Toggling the active level clears it. */
  private rtHeading(e: MouseEvent, h: 1 | 2 | 3): void {
    e.preventDefault();
    if (!this.rtBar) return;
    const cur = this.regionStyleOf(this.rtBar.region).h;
    this.setRegionStyle(this.rtBar.region, { h: cur === h ? undefined : h });
  }

  /** Set the text alignment for the WHOLE region — writes regionStyles. Toggling
   *  the active alignment clears it (back to the slide default). */
  private rtAlign(e: MouseEvent, align: 'left' | 'center' | 'right'): void {
    e.preventDefault();
    if (!this.rtBar) return;
    const cur = this.regionStyleOf(this.rtBar.region).align;
    this.setRegionStyle(this.rtBar.region, { align: cur === align ? undefined : align });
  }

  /** Position the floating toolbar over the current selection rect: anchored to
   *  the selection's left edge, placed ABOVE it (so it never covers the text being
   *  edited), flipping below when there's no room above. Clamped to the viewport. */
  private rtPos(w: number, hgt: number): { left: number; top: number } {
    const r = this.rtRect || { left: 200, top: 220, bottom: 248 };
    const vw = window.innerWidth, vh = window.innerHeight;
    const left = Math.min(Math.max(r.left - 8, 12), vw - w - 12);
    let top = r.top - hgt - 10;
    if (top < 12) top = Math.min(r.bottom + 10, vh - hgt - 12);
    return { left, top };
  }

  /** The floating selection toolbar: inline marks (bold/italic/underline/strike/
   *  link/colour) applied to the selection, plus heading size + alignment applied
   *  to the WHOLE region (via regionStyles). Rendered only while a rich region is
   *  inline-edited with a non-collapsed selection. Buttons use mousedown +
   *  preventDefault so clicking never blurs the editable / collapses the
   *  selection. */
  private renderRichToolbar(): TemplateResult {
    const region = this.rtBar!.region;
    const rs = this.regionStyleOf(region);
    const p = this.rtPos(this.rtColourOpen ? 360 : 340, 44);
    // The Paper-derived brand palette (concrete hexes so the committed colour
    // survives the colour-only sanitiser) + a "default" that unwraps the colour.
    return html`
      <div class="pop rt-bar" role="toolbar" aria-label="Format selection"
        style="position:fixed;left:${p.left}px;top:${p.top}px"
        @mousedown=${(e: MouseEvent) => e.preventDefault()}
        @click=${(e: Event) => e.stopPropagation()}>
        <button class="rt-btn" title="Bold" aria-label="Bold" @mousedown=${(e: MouseEvent) => this.rtCmd(e, 'bold')}><b>B</b></button>
        <button class="rt-btn" title="Italic" aria-label="Italic" @mousedown=${(e: MouseEvent) => this.rtCmd(e, 'italic')}><i>I</i></button>
        <button class="rt-btn" title="Underline" aria-label="Underline" @mousedown=${(e: MouseEvent) => this.rtCmd(e, 'underline')}><u>U</u></button>
        <button class="rt-btn" title="Strikethrough" aria-label="Strikethrough" @mousedown=${(e: MouseEvent) => this.rtCmd(e, 'strikethrough')}><s>S</s></button>
        <button class="rt-btn" title="Link" aria-label="Add a link" @mousedown=${(e: MouseEvent) => this.rtLink(e)}>🔗</button>
        <span class="rt-colour-wrap">
          <button class="rt-btn rt-colour-btn" title="Text colour" aria-label="Text colour" aria-expanded=${this.rtColourOpen}
            @mousedown=${(e: MouseEvent) => { e.preventDefault(); this.rtColourOpen = !this.rtColourOpen; }}><span class="rt-colour-glyph">A</span></button>
          ${this.rtColourOpen ? html`
            <div class="rt-colours" role="menu" aria-label="Text colour" @mousedown=${(e: MouseEvent) => e.preventDefault()}>
              ${ACCENTS.map((a) => html`
                <button class="rt-colour-sw" style="background:${a.hex}" title=${a.name} aria-label=${a.name}
                  @mousedown=${(e: MouseEvent) => this.rtColour(e, a.hex)}></button>`)}
              <button class="rt-colour-default" title="Clear formatting on the selection" aria-label="Clear formatting"
                @mousedown=${(e: MouseEvent) => { e.preventDefault(); const n = this.rtEditNode(); if (n) { document.execCommand('removeFormat', false); this.rtColourOpen = false; this.rtRecommit(n); } }}>Clear</button>
            </div>` : nothing}
        </span>
        <span class="rt-sep" role="separator"></span>
        <button class="rt-btn ${rs.h === 1 ? 'on' : ''}" title="Heading 1" aria-label="Heading 1" aria-pressed=${rs.h === 1} @mousedown=${(e: MouseEvent) => this.rtHeading(e, 1)}>H1</button>
        <button class="rt-btn ${rs.h === 2 ? 'on' : ''}" title="Heading 2" aria-label="Heading 2" aria-pressed=${rs.h === 2} @mousedown=${(e: MouseEvent) => this.rtHeading(e, 2)}>H2</button>
        <button class="rt-btn ${rs.h === 3 ? 'on' : ''}" title="Heading 3" aria-label="Heading 3" aria-pressed=${rs.h === 3} @mousedown=${(e: MouseEvent) => this.rtHeading(e, 3)}>H3</button>
        <span class="rt-sep" role="separator"></span>
        <button class="rt-btn ${rs.align === 'left' ? 'on' : ''}" title="Align left" aria-label="Align left" aria-pressed=${rs.align === 'left'} @mousedown=${(e: MouseEvent) => this.rtAlign(e, 'left')}>⇤</button>
        <button class="rt-btn ${rs.align === 'center' ? 'on' : ''}" title="Align centre" aria-label="Align centre" aria-pressed=${rs.align === 'center'} @mousedown=${(e: MouseEvent) => this.rtAlign(e, 'center')}>⇔</button>
        <button class="rt-btn ${rs.align === 'right' ? 'on' : ''}" title="Align right" aria-label="Align right" aria-pressed=${rs.align === 'right'} @mousedown=${(e: MouseEvent) => this.rtAlign(e, 'right')}>⇥</button>
      </div>`;
  }

  private renderEditPop(): TemplateResult {
    const ask = this.sel!.mode === 'ask';
    const p = this.popPos(ask ? 320 : 240, ask ? 130 : 48);
    return html`
      <div class="pop" role="menu" style="position:fixed;left:${p.left}px;top:${p.top}px" @click=${(e: Event) => e.stopPropagation()}>
        ${ask
          ? html`<div class="pop-ask">
              <div class="ask-in">
                <input autofocus placeholder="How should I change this?" .value=${this.askVal}
                  @input=${(e: Event) => { this.askVal = (e.target as HTMLInputElement).value; }}
                  @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' && this.askVal.trim()) this.askRegion(this.askVal); }}>
                <button class="send-btn" aria-label="Send" @click=${() => this.askVal.trim() && this.askRegion(this.askVal)}>↑</button>
              </div>
              <div class="pop-sugs">
                <button class="pop-sug" @click=${() => this.askRegion('punchier')}>Make it punchier</button>
                <button class="pop-sug" @click=${() => this.askRegion('warmer')}>Warmer</button>
                <button class="pop-sug" @click=${() => this.askRegion('more formal')}>More formal</button>
              </div></div>`
          : html`<div class="pop-bar">
              <button class="pop-btn ai" @click=${() => { this.sel = { ...this.sel!, mode: 'ask' }; }}>✦ Ask AI</button>
              <span class="sep"></span>
              <button class="pop-btn" @click=${() => this.startTextEdit()}>✎ Edit text</button>
            </div>`}
      </div>`;
  }

  private renderDiffPop(): TemplateResult {
    const p = this.popPos(320, 160);
    return html`
      <div class="pop diff-pop" role="dialog" aria-label="Suggested change" style="position:fixed;left:${p.left}px;top:${p.top}px" @click=${(e: Event) => e.stopPropagation()}>
        <div class="diff-head">✦ Suggested change</div>
        <div class="diff-old">${this.diff!.old}</div>
        <div class="diff-new">${this.diff!.html}</div>
        <div class="diff-actions">
          <button class="btn btn--primary btn--sm" @click=${() => this.resolveDiff(true)}>✓ Accept</button>
          <button class="btn btn--ghost btn--sm" @click=${() => this.resolveDiff(false)}>Discard</button>
        </div>
      </div>`;
  }

  // Right-click → open the visual "change slide type" gallery.
  private onContext(e: MouseEvent): void {
    e.preventDefault();
    this.sel = null;
    this.libMode = 'replace';
    this.libOpen = true;
  }

  private renderLibrary(): TemplateResult {
    return html`
      <div class="scrim" @mousedown=${(e: MouseEvent) => { if (e.target === e.currentTarget) this.libOpen = false; }}>
        <div class="lib" role="dialog" aria-modal="true" aria-label="Slide gallery">
          <div class="lib-head">
            <span class="ai-orb" style="width:28px;height:28px;border-radius:8px">▤</span>
            <div><div class="ttl">${this.libMode === 'replace' ? 'Change slide type' : 'Insert a slide'}</div><div class="sub">${this.libMode === 'replace' ? 'Pick a layout — keeps your title' : 'Reusable layouts · stays in your theme'}</div></div>
            <span class="spacer" style="flex:1"></span>
            <button class="icon-btn" aria-label="Close" @click=${() => { this.libOpen = false; }}>×</button>
          </div>
          <div class="lib-body">
            ${SLIDE_LIBRARY.map((g) => html`
              <div class="lib-group">
                <div class="t-tag">${g.group}</div>
                <div class="lib-grid">
                  ${g.items.map((t) => html`
                    <button class="lib-card" @click=${() => this.insertLibrary(t)}>
                      <div class="lc-thumb" data-thumb="lib:${t.id}"></div>
                      <div class="lc-meta"><div class="lc-name">${t.name}</div><div class="lc-desc">${t.desc}</div></div>
                    </button>`)}
                </div>
              </div>`)}
          </div>
        </div>
      </div>`;
  }

  private renderConfirm(): TemplateResult {
    const yes = this.confirm!.yes ?? 'Delete';
    // The danger styling is the destructive default (delete); a non-destructive
    // confirm (e.g. generate-all) uses the primary button instead.
    const danger = yes === 'Delete';
    return html`
      <div class="scrim" @mousedown=${(e: MouseEvent) => { if (e.target === e.currentTarget) this.confirm = null; }}>
        <div class="confirm-dialog" role="alertdialog" aria-modal="true" aria-label="Confirm action">
          <div class="confirm-msg">${this.confirm!.msg}</div>
          <div class="confirm-actions">
            <button class="btn btn--ghost btn--sm" @click=${() => { this.confirm = null; }}>Cancel</button>
            <button class="btn ${danger ? 'btn--danger' : 'btn--ai'} btn--sm" @click=${() => this.confirm!.onYes()}>${yes}</button>
          </div>
        </div>
      </div>`;
  }

  private renderNotes(): TemplateResult {
    const slide = this.focusSlide!;
    return html`
      <div class="notes-editor" @click=${(e: Event) => e.stopPropagation()}>
        <div class="ne-head">
          <span class="ne-title">Speaker notes</span>
          <span class="ne-meta faint">${(slide.label || '').split('·')[0].trim()}</span>
          <span class="spacer" style="flex:1"></span>
          <button class="icon-btn" aria-label="Close notes" @click=${() => { this.notesOpen = false; }}>▾</button>
        </div>
        <div class="ne-toolbar" role="toolbar" aria-label="Note formatting">
          <button class="ne-tool" title="Bold (⌘B)" aria-label="Bold" @mousedown=${(e: MouseEvent) => this.noteCmd(e, 'bold')}><b>B</b></button>
          <button class="ne-tool" title="Italic (⌘I)" aria-label="Italic" @mousedown=${(e: MouseEvent) => this.noteCmd(e, 'italic')}><i>I</i></button>
          <button class="ne-tool" title="Bulleted list" aria-label="Bulleted list" @mousedown=${(e: MouseEvent) => this.noteCmd(e, 'insertUnorderedList')}>•</button>
          <button class="ne-tool" title="Numbered list" aria-label="Numbered list" @mousedown=${(e: MouseEvent) => this.noteCmd(e, 'insertOrderedList')}>1.</button>
        </div>
        <div class="ne-rich" contenteditable="true" role="textbox" aria-multiline="true" aria-label="Speaker notes"
          data-placeholder="Write what you'll say on this slide. Notes show in the presenter, not on the slide."
          @input=${(e: Event) => this.onNoteInput(e)}></div>
      </div>`;
  }

  /** Apply a rich-text command without losing the caret (mousedown + preventDefault
   *  keeps focus in the editable). */
  private noteCmd(e: MouseEvent, cmd: string): void {
    e.preventDefault();
    const rich = this.querySelector<HTMLElement>('.ne-rich');
    rich?.focus();
    document.execCommand(cmd, false);
    if (rich) this.commitNote(rich);
  }
  private onNoteInput(e: Event): void { this.commitNote(e.target as HTMLElement); }
  private commitNote(el: HTMLElement): void { this.setNote(sanitiseNoteHtml(el.innerHTML)); }
}

// Minimal, safe markdown for chat replies: escape first, then apply a small
// inline subset (bold, italic, code, links) + paragraphs/lists. No raw HTML
// from the model ever reaches the DOM unescaped.
function mdToHtml(src: string): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const inline = (s: string) => esc(s)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  const blocks = src.trim().split(/\n{2,}/);
  return blocks.map((b) => {
    const lines = b.split('\n');
    if (lines.every((l) => /^\s*[-*]\s+/.test(l))) {
      return '<ul>' + lines.map((l) => '<li>' + inline(l.replace(/^\s*[-*]\s+/, '')) + '</li>').join('') + '</ul>';
    }
    return '<p>' + lines.map(inline).join('<br>') + '</p>';
  }).join('');
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

/** Read a File into a `data:` URL (used for embedded slide background images so
 *  the exported artifact stays self-contained). */
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result || ''));
    fr.onerror = () => reject(fr.error || new Error('read failed'));
    fr.readAsDataURL(file);
  });
}

/** Read a logo File into a DOWNSCALED data-URL so it stays small enough to embed
 *  in the self-contained artifact (the deck-source bucket exposes no public image
 *  URL to reference). A bitmap is drawn to a canvas capped at ~160px tall (logos
 *  are shown at ~26–40px, so 160px covers retina); an SVG is returned verbatim as
 *  a data-URL (vector — already tiny + lossless). Falls back to the raw data-URL
 *  if the canvas path fails. */
async function fileToDownscaledLogo(file: File, maxH = 160): Promise<string> {
  const dataUrl = await fileToDataUrl(file);
  if (file.type === 'image/svg+xml' || dataUrl.startsWith('data:image/svg')) return dataUrl;
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error('decode failed'));
      im.src = dataUrl;
    });
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h || h <= maxH) return dataUrl; // already small enough
    const scale = maxH / h;
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw; canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;
    ctx.drawImage(img, 0, 0, cw, ch);
    return canvas.toDataURL('image/png');
  } catch {
    return dataUrl; // best effort — embed the original rather than fail
  }
}

// Allow-list sanitiser for speaker-note rich text. Notes are author-written and
// rendered as HTML in the presenter (incl. the shared artifact), so we keep only
// safe inline/list formatting and drop everything else — no attributes, no
// scripts, no styles. Block-ish tags are normalised; <div> becomes <p>.
const NOTE_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'UL', 'OL', 'LI', 'P', 'BR']);
function sanitiseNoteHtml(html: string): string {
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const walk = (node: Node): string => {
    let out = '';
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        out += (child.textContent || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) return;
      let tag = (child as Element).tagName;
      if (tag === 'DIV') tag = 'P';
      if (tag === 'BR') { out += '<br>'; return; }
      if (NOTE_TAGS.has(tag)) {
        const t = tag.toLowerCase();
        out += `<${t}>${walk(child)}</${t}>`;
      } else {
        out += walk(child); // unwrap unknown tags, keep their text
      }
    });
    return out;
  };
  return walk(tpl.content).trim();
}

declare global {
  interface HTMLElementTagNameMap {
    'studio-page': StudioPage;
  }
}
